import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import axiosInstance from './axiosInstance';
import InsurerBreakdownTab from './InsurerBreakdownTab';
import ProfitBreakdownTab from './ProfitBreakdownTab';
import SapBreakdownTab from './SapBreakdownTab';
import { useAuthSession } from './AuthSessionContext';

const CHART_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
  '#bcbd22', '#17becf', '#393b79', '#637939', '#8c6d31', '#843c39', '#7b4173', '#3182bd',
  '#e6550d', '#31a354', '#756bb1', '#636363', '#9ecae1', '#ffbb78', '#98df8a', '#ff9896',
  '#c5b0d5', '#c49c94', '#f7b6d2', '#c7c7c7', '#dbdb8d', '#9edae5', '#6baed6', '#fd8d3c',
  '#74c476', '#bcbddc', '#969696', '#17becf', '#ff7f50', '#2f4f4f', '#ff1493', '#708090'
];

const CHART_CARD_MIN_WIDTH = 920;

const toFilterArray = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined && String(item).trim() !== '');
  if (value === null || value === undefined || String(value).trim() === '') return [];
  return [value];
};

const filterParamValue = (value) => {
  const values = toFilterArray(value);
  return values.length ? values.join(',') : undefined;
};



const HISTOGRAM_LEFT_MARGIN = 64;

const CHART_FONT_SIZES = {
  value: 14,
  axis: 12,
  pie: 16,
  legend: 16,
};

const SUBCONTRACTED_BASE_CODES = ['50722070', '50710000', '50722090', '50722100', '50722060', '50722080'];
const buildDefaultCategoryCodeSets = (groupCodes) => ({
  car_parts: ['50622300'],
  sub_contracted: Array.isArray(groupCodes) && groupCodes.length
    ? groupCodes
    : SUBCONTRACTED_BASE_CODES,
  sub_contracted_adas_calibration_diagnostics: ['50722070'],
  sub_contracted_general: ['50710000'],
  sub_contracted_lock_set: ['50722090'],
  sub_contracted_signwriting_graphics: ['50722100'],
  sub_contracted_tyres_alloy_wheels: ['50722060'],
  sub_contracted_windscreen_repairs: ['50722080'],
  storage_and_recovery: ['50722110'],
});

const SUBCONTRACTED_NAME_CODE_MAP = {
  'subcontracted work - adas/calibration/diagnostics': '50722070',
  'subcontracted work - general': '50710000',
  'subcontracted work - lock set': '50722090',
  'subcontracted work - signwriting/graphics': '50722100',
  'subcontracted work - tyres/alloy wheels': '50722060',
  'subcontracted work - windscreen repairs': '50722080',
};
const SUBCONTRACTED_KEY_CODE_MAP = {
  sub_contracted_adas_calibration_diagnostics: '50722070',
  sub_contracted_general: '50710000',
  sub_contracted_lock_set: '50722090',
  sub_contracted_signwriting_graphics: '50722100',
  sub_contracted_tyres_alloy_wheels: '50722060',
  sub_contracted_windscreen_repairs: '50722080',
};

const buildNominalCategoryNameMap = (groupCodes = []) => ({
  'car parts': ['car_parts'],
  'subcontracted work - adas/calibration/diagnostics': (groupCodes || []).includes('50722070')
    ? ['sub_contracted', 'sub_contracted_adas_calibration_diagnostics']
    : ['sub_contracted_adas_calibration_diagnostics'],
  'subcontracted work - general': (groupCodes || []).includes('50710000')
    ? ['sub_contracted', 'sub_contracted_general']
    : ['sub_contracted_general'],
  'subcontracted work - lock set': (groupCodes || []).includes('50722090')
    ? ['sub_contracted', 'sub_contracted_lock_set']
    : ['sub_contracted_lock_set'],
  'subcontracted work - signwriting/graphics': (groupCodes || []).includes('50722100')
    ? ['sub_contracted', 'sub_contracted_signwriting_graphics']
    : ['sub_contracted_signwriting_graphics'],
  'subcontracted work - tyres/alloy wheels': (groupCodes || []).includes('50722060')
    ? ['sub_contracted', 'sub_contracted_tyres_alloy_wheels']
    : ['sub_contracted_tyres_alloy_wheels'],
  'subcontracted work - windscreen repairs': (groupCodes || []).includes('50722080')
    ? ['sub_contracted', 'sub_contracted_windscreen_repairs']
    : ['sub_contracted_windscreen_repairs'],
  'storage & recovery': ['storage_and_recovery'],
  'storage and recovery': ['storage_and_recovery'],
});

const BASE_BREAKDOWN_FIELD_CONFIG = [
  {
    key: 'car_parts',
    title: 'Car Parts',
    invoiceKey: 'car_parts_invoice_amount',
    creditKey: 'car_parts_credit_note_amount',
    approvedKey: 'car_parts_approved_amount',
    notApprovedKey: 'car_parts_not_approved_amount',
    blockedKey: 'car_parts_blocked_amount',
    invoiceLabel: 'Car Parts Invoice Amount',
    creditLabel: 'Car Parts Credit Amount',
    codeSetKey: 'car_parts',
  },
  {
    key: 'car_parts_genuine',
    title: 'Car Parts (Genuine)',
    invoiceKey: 'car_parts_genuine_invoice_amount',
    creditKey: 'car_parts_genuine_credit_note_amount',
    approvedKey: 'car_parts_genuine_approved_amount',
    notApprovedKey: 'car_parts_genuine_not_approved_amount',
    blockedKey: 'car_parts_genuine_blocked_amount',
    invoiceLabel: 'Car Parts (Genuine) Invoice Amount',
    creditLabel: 'Car Parts (Genuine) Credit Amount',
    codeSetKey: 'car_parts',
    parentKey: 'car_parts',
  },
  {
    key: 'car_parts_non_genuine',
    title: 'Car Parts (Non Genuine)',
    invoiceKey: 'car_parts_non_genuine_invoice_amount',
    creditKey: 'car_parts_non_genuine_credit_note_amount',
    approvedKey: 'car_parts_non_genuine_approved_amount',
    notApprovedKey: 'car_parts_non_genuine_not_approved_amount',
    blockedKey: 'car_parts_non_genuine_blocked_amount',
    invoiceLabel: 'Car Parts (Non Genuine) Invoice Amount',
    creditLabel: 'Car Parts (Non Genuine) Credit Amount',
    codeSetKey: 'car_parts',
    parentKey: 'car_parts',
  },
  {
    key: 'car_parts_green',
    title: 'Car Parts (Green)',
    invoiceKey: 'car_parts_green_invoice_amount',
    creditKey: 'car_parts_green_credit_note_amount',
    approvedKey: 'car_parts_green_approved_amount',
    notApprovedKey: 'car_parts_green_not_approved_amount',
    blockedKey: 'car_parts_green_blocked_amount',
    invoiceLabel: 'Car Parts (Green) Invoice Amount',
    creditLabel: 'Car Parts (Green) Credit Amount',
    codeSetKey: 'car_parts',
    parentKey: 'car_parts',
  },
  {
    key: 'sub_contracted',
    title: 'Subcontracted',
    invoiceKey: 'sub_contracted_invoice_amount',
    creditKey: 'sub_contracted_credit_note_amount',
    approvedKey: 'sub_contracted_approved_amount',
    notApprovedKey: 'sub_contracted_not_approved_amount',
    blockedKey: 'sub_contracted_blocked_amount',
    invoiceLabel: 'Subcontracted Invoice Amount',
    creditLabel: 'Subcontracted Credit Amount',
    codeSetKey: 'sub_contracted',
  },
  {
    key: 'sub_contracted_general',
    title: 'Subcontracted (General)',
    invoiceKey: 'sub_contracted_general_invoice_amount',
    creditKey: 'sub_contracted_general_credit_note_amount',
    approvedKey: 'sub_contracted_general_approved_amount',
    notApprovedKey: 'sub_contracted_general_not_approved_amount',
    blockedKey: 'sub_contracted_general_blocked_amount',
    invoiceLabel: 'Subcontracted (General) Invoice Amount',
    creditLabel: 'Subcontracted (General) Credit Amount',
    codeSetKey: 'sub_contracted_general',
    parentKey: 'sub_contracted',
  },
  {
    key: 'sub_contracted_lock_set',
    title: 'Subcontracted (Lock Set)',
    invoiceKey: 'sub_contracted_lock_set_invoice_amount',
    creditKey: 'sub_contracted_lock_set_credit_note_amount',
    approvedKey: 'sub_contracted_lock_set_approved_amount',
    notApprovedKey: 'sub_contracted_lock_set_not_approved_amount',
    blockedKey: 'sub_contracted_lock_set_blocked_amount',
    invoiceLabel: 'Subcontracted (Lock Set) Invoice Amount',
    creditLabel: 'Subcontracted (Lock Set) Credit Amount',
    codeSetKey: 'sub_contracted_lock_set',
    parentKey: 'sub_contracted',
  },
  {
    key: 'sub_contracted_signwriting_graphics',
    title: 'Subcontracted (Signwriting/Graphics)',
    invoiceKey: 'sub_contracted_signwriting_graphics_invoice_amount',
    creditKey: 'sub_contracted_signwriting_graphics_credit_note_amount',
    approvedKey: 'sub_contracted_signwriting_graphics_approved_amount',
    notApprovedKey: 'sub_contracted_signwriting_graphics_not_approved_amount',
    blockedKey: 'sub_contracted_signwriting_graphics_blocked_amount',
    invoiceLabel: 'Subcontracted (Signwriting/Graphics) Invoice Amount',
    creditLabel: 'Subcontracted (Signwriting/Graphics) Credit Amount',
    codeSetKey: 'sub_contracted_signwriting_graphics',
    parentKey: 'sub_contracted',
  },
  {
    key: 'sub_contracted_tyres_alloy_wheels',
    title: 'Subcontracted (Tyres/Alloy Wheels)',
    invoiceKey: 'sub_contracted_tyres_alloy_wheels_invoice_amount',
    creditKey: 'sub_contracted_tyres_alloy_wheels_credit_note_amount',
    approvedKey: 'sub_contracted_tyres_alloy_wheels_approved_amount',
    notApprovedKey: 'sub_contracted_tyres_alloy_wheels_not_approved_amount',
    blockedKey: 'sub_contracted_tyres_alloy_wheels_blocked_amount',
    invoiceLabel: 'Subcontracted (Tyres/Alloy Wheels) Invoice Amount',
    creditLabel: 'Subcontracted (Tyres/Alloy Wheels) Credit Amount',
    codeSetKey: 'sub_contracted_tyres_alloy_wheels',
    parentKey: 'sub_contracted',
  },
  {
    key: 'sub_contracted_windscreen_repairs',
    title: 'Subcontracted (Windscreen Repairs)',
    invoiceKey: 'sub_contracted_windscreen_repairs_invoice_amount',
    creditKey: 'sub_contracted_windscreen_repairs_credit_note_amount',
    approvedKey: 'sub_contracted_windscreen_repairs_approved_amount',
    notApprovedKey: 'sub_contracted_windscreen_repairs_not_approved_amount',
    blockedKey: 'sub_contracted_windscreen_repairs_blocked_amount',
    invoiceLabel: 'Subcontracted (Windscreen Repairs) Invoice Amount',
    creditLabel: 'Subcontracted (Windscreen Repairs) Credit Amount',
    codeSetKey: 'sub_contracted_windscreen_repairs',
    parentKey: 'sub_contracted',
  },
  {
    key: 'sub_contracted_adas_calibration_diagnostics',
    title: 'ADAS/Calibration/Diagnostics',
    invoiceKey: 'sub_contracted_adas_calibration_diagnostics_invoice_amount',
    creditKey: 'sub_contracted_adas_calibration_diagnostics_credit_note_amount',
    approvedKey: 'sub_contracted_adas_calibration_diagnostics_approved_amount',
    notApprovedKey: 'sub_contracted_adas_calibration_diagnostics_not_approved_amount',
    blockedKey: 'sub_contracted_adas_calibration_diagnostics_blocked_amount',
    invoiceLabel: 'ADAS/Calibration/Diagnostics Invoice Amount',
    creditLabel: 'ADAS/Calibration/Diagnostics Credit Amount',
    codeSetKey: 'sub_contracted_adas_calibration_diagnostics',
  },
  {
    key: 'storage_and_recovery',
    title: 'Storage & Recovery',
    invoiceKey: 'storage_and_recovery_invoice_amount',
    creditKey: 'storage_and_recovery_credit_note_amount',
    approvedKey: 'storage_and_recovery_approved_amount',
    notApprovedKey: 'storage_and_recovery_not_approved_amount',
    blockedKey: 'storage_and_recovery_blocked_amount',
    invoiceLabel: 'Storage & Recovery Invoice Amount',
    creditLabel: 'Storage & Recovery Credit Amount',
    codeSetKey: 'storage_and_recovery',
  },
];

const UNKNOWN_NOMINAL_CODE = '__unknown__';
const UNNAMED_FILTER_VALUE = '__unnamed__';
const UNNAMED_SUPPLIER_LABEL = 'Unknown Supplier';
const UNNAMED_NOMINAL_CODE_LABEL = 'Unknown Nominal Code';
const NOT_ALLOWED_SUPPLIER_STATUSES = new Set(['not_allowed']);

const normalizeNominalCodeId = (value) =>
  value === null || value === undefined || value === '' ? UNKNOWN_NOMINAL_CODE : value;

const formatNominalCodeLabel = (codeId) =>
  codeId === UNKNOWN_NOMINAL_CODE ? 'Other' : codeId;

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const formatPercentValue = (value, total) =>
  total && Number.isFinite(total) && Math.abs(total) > 0.000001
    ? `${((value / total) * 100).toFixed(2)}%`
    : '-';

const formatDiscountPercent = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '-'
    : `${Number(value).toFixed(2)}%`;

const escapeCsvValue = (value) => {
  const stringValue = value === null || value === undefined ? '' : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const BASE_EXPORT_CATEGORY_FIELDS = [
  { key: 'car_parts', label: 'Car Parts' },
  { key: 'sub_contracted', label: 'Subcontracted' },
  { key: 'sub_contracted_adas_calibration_diagnostics', label: 'Subcontracted - ADAS/Calibration/Diagnostics' },
  { key: 'storage_and_recovery', label: 'Storage & Recovery' },
];

const stickyTotalRowSx = {
  position: 'sticky',
  bottom: 0,
  zIndex: 1,
  backgroundColor: 'background.paper',
  borderTop: '2px solid rgba(0,0,0,0.2)',
  '& td': {
    fontWeight: 600,
    backgroundColor: 'background.paper',
  },
};

const normalizeNominalCategoryName = (name) =>
  name
    ? name
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/\s+/g, ' ')
        .trim()
    : '';

const buildCategoryCodeSetsFromApi = (nominalCodes = [], nameMap = {}) => {
  const sets = {};
  nominalCodes.forEach((nominal) => {
    if (!nominal?.nominal_code_id) return;
    const normalizedName = normalizeNominalCategoryName(nominal.name);
    const targetKeys = nameMap[normalizedName];
    if (!targetKeys || !targetKeys.length) return;
    const codeId = normalizeNominalCodeId(nominal.nominal_code_id);
    targetKeys.forEach((key) => {
      if (!sets[key]) {
        sets[key] = new Set();
      }
      sets[key].add(codeId);
    });
  });
  return Object.entries(sets).reduce((acc, [key, value]) => {
    acc[key] = Array.from(value);
    return acc;
  }, {});
};

const QUICK_RANGE_OPTIONS = [
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Last 60 Days', value: 'last_60' },
  { label: 'Last 90 Days', value: 'last_90' },
  { label: 'Custom', value: 'custom' },
];

const COST_BY_FILTER_OPTIONS = [
  { label: 'Repair Site Address', value: 'repair' },
  { label: 'Invoice Delivery Address', value: 'delivery' },
  { label: 'Invoice Billing Address', value: 'billing' },
];

const LOCATION_SORT_OPTIONS = [
  { label: 'By Cost', value: 'cost' },
  { label: 'Alphabetical', value: 'alpha' },
];

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getRangeDates = (rangeKey) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (rangeKey) {
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: formatDate(start), end: formatDate(today) };
    }
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: formatDate(start), end: formatDate(end) };
    }
    case 'last_90': {
      const start = new Date(today);
      start.setDate(start.getDate() - 89);
      return { start: formatDate(start), end: formatDate(today) };
    }
    case 'last_60':
    default: {
      const start = new Date(today);
      start.setDate(start.getDate() - 59);
      return { start: formatDate(start), end: formatDate(today) };
    }
  }
};

const toNumber = (value) => {
  if (typeof value === 'string') {
    const normalised = value.replace(/[^0-9.-]/g, '');
    const parsed = Number(normalised);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseDiscountValue = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const computeDiscountReceived = (actualTotal, retailTotal) => {
  const actual = toNumber(actualTotal);
  const retail = toNumber(retailTotal);
  if (!retail || retail <= 0) return null;
  const value = (1 - actual / retail) * 100;
  return Number.isFinite(value) ? Number(value.toFixed(2)) : null;
};

const Dashboard = () => {
  const initialRange = useMemo(() => getRangeDates('this_month'), []);
  const { roles: userRoles = [] } = useAuthSession();
  const canViewBreakdownTabs = userRoles.includes('admin') || userRoles.includes('reviewer');
  const canViewProfitBreakdown = userRoles.includes('admin');

  const [locations, setLocations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [granularityOptions, setGranularityOptions] = useState([]);
  const [nominalCodes, setNominalCodes] = useState([]);
  const [subcontractedNominalCodes, setSubcontractedNominalCodes] = useState([]);
  const [adasNominalCode, setAdasNominalCode] = useState('50722070');

  const [filters, setFilters] = useState({
    regionId: [],
    locationId: [],
    supplierId: [],
    nominalCodeId: [],
    granularity: '',
    startDate: initialRange.start,
    endDate: initialRange.end,
    costBy: 'repair',
  });

  const [quickRange, setQuickRange] = useState('this_month');
  const [locationSortMode, setLocationSortMode] = useState('cost');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState('');
  const [approvalPercentMode, setApprovalPercentMode] = useState('split');
  const [hoverTooltip, setHoverTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: { header: '', lines: [] },
  });
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [isDrilldownVisible, setIsDrilldownVisible] = useState(false);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(null);
  const [selectedNominalCodeId, setSelectedNominalCodeId] = useState(null);
  const [globalCategoryKey, setGlobalCategoryKey] = useState(null);
  const [globalNominalCodeId, setGlobalNominalCodeId] = useState(null);
  const [isAggregateBreakdownVisible] = useState(true);
  const [filterCardWidth, setFilterCardWidth] = useState(null);
  const [filterCardOuterWidth, setFilterCardOuterWidth] = useState(null);
  const [insurerFilters, setInsurerFilters] = useState({
    insurerName: '',
    vehicleManufacturer: '',
    driveable: '',
    jobType: '',
  });
  const [insurerFilterOptions, setInsurerFilterOptions] = useState({
    insurers: [],
    manufacturers: [],
    driveables: [],
    jobTypes: [],
    sourceCodes: [],
  });
  const [supplierSortConfig, setSupplierSortConfig] = useState({ key: 'percent', direction: 'desc' });
  const [budgetsByLocation, setBudgetsByLocation] = useState(() => new Map());
  const [budgetsLoading, setBudgetsLoading] = useState(false);
  const [exportingBreakdown, setExportingBreakdown] = useState(false);
  const drilldownRef = useRef(null);
  const locationBreakdownRef = useRef(null);
  const aggregateBreakdownRef = useRef(null);
  const locationNominalTableRef = useRef(null);
  const locationSupplierTableRef = useRef(null);
  const aggregateNominalTableRef = useRef(null);
  const aggregateSupplierTableRef = useRef(null);
  const filterCardRef = useRef(null);
  const filtersRef = useRef(filters);
  const lastDashboardFiltersRef = useRef(null);
  const [appliedFilters, setAppliedFilters] = useState(filtersRef.current);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const requestedTab = searchParams.get('tab');
    const allowedTabs = [
      'dashboard',
      ...(canViewProfitBreakdown ? ['profit'] : []),
      ...(canViewBreakdownTabs ? ['insurer', 'sap'] : []),
    ];
    const nextTab = allowedTabs.includes(requestedTab) ? requestedTab : 'dashboard';
    setActiveTab((currentTab) => (currentTab === nextTab ? currentTab : nextTab));
  }, [canViewBreakdownTabs, canViewProfitBreakdown, location.search]);

  useEffect(() => {
    if (
      (activeTab === 'profit' && !canViewProfitBreakdown)
      || (['insurer', 'sap'].includes(activeTab) && !canViewBreakdownTabs)
    ) {
      setActiveTab('dashboard');
    }
  }, [activeTab, canViewBreakdownTabs, canViewProfitBreakdown]);

  const breakdownPanelWidth = useMemo(() => {
    if (!filterCardOuterWidth) return undefined;
    return filterCardOuterWidth;
  }, [filterCardOuterWidth]);

  const findFilterOption = (options, value) =>
    options.find((option) => option.value === value) || null;

  const locationNameMap = useMemo(() => {
    const map = new Map();
    locations.forEach((loc) => {
      if (loc?.location_id) {
        map.set(loc.location_id, loc.name || loc.location_id);
      }
    });
    return map;
  }, [locations]);

  const regionNameMap = useMemo(() => {
    const map = new Map();
    regions.forEach((region) => {
      if (region?.region_id) {
        map.set(region.region_id, region.name || region.region_id);
      }
    });
    return map;
  }, [regions]);

  const availableRegions = useMemo(
    () =>
      regions
        .filter((region) => {
          const regionId = (region?.region_id || '').trim();
          const regionName = (region?.name || '').trim().toLowerCase();
          return regionId && regionName !== 'unknown';
        })
        .sort((a, b) => (a.name || a.region_id || '').localeCompare(b.name || b.region_id || '')),
    [regions]
  );

  const filteredLocations = useMemo(() => {
    const selectedRegionIds = toFilterArray(filters.regionId);
    if (selectedRegionIds.length === 0) {
      return locations;
    }
    const selectedRegionSet = new Set(selectedRegionIds);
    return locations.filter((location) => selectedRegionSet.has(location?.region_id));
  }, [filters.regionId, locations]);

  const locationRegionNameMap = useMemo(() => {
    const map = new Map();
    locations.forEach((loc) => {
      if (!loc?.location_id) return;
      const regionId = loc.region_id;
      const regionName = regionNameMap.get(regionId) || regionId || '';
      map.set(loc.location_id, regionName);
    });
    return map;
  }, [locations, regionNameMap]);

  const supplierFilterOptions = useMemo(
    () => [
      { supplier_id: UNNAMED_FILTER_VALUE, name: UNNAMED_SUPPLIER_LABEL },
      ...suppliers,
    ],
    [suppliers]
  );

  const nominalCodeFilterOptions = useMemo(
    () => [
      { nominal_code_id: UNNAMED_FILTER_VALUE, name: UNNAMED_NOMINAL_CODE_LABEL },
      ...nominalCodes,
    ],
    [nominalCodes]
  );


  const selectedRegionOptions = useMemo(() => {
    const selected = new Set(toFilterArray(filters.regionId));
    return availableRegions.filter((region) => selected.has(region.region_id));
  }, [availableRegions, filters.regionId]);

  const selectedLocationOptions = useMemo(() => {
    const selected = new Set(toFilterArray(filters.locationId));
    return filteredLocations.filter((location) => selected.has(location.location_id));
  }, [filteredLocations, filters.locationId]);

  const selectedSupplierOptions = useMemo(() => {
    const selected = new Set(toFilterArray(filters.supplierId));
    return supplierFilterOptions.filter((supplier) => selected.has(supplier.supplier_id));
  }, [supplierFilterOptions, filters.supplierId]);

  const selectedNominalCodeOptions = useMemo(() => {
    const selected = new Set(toFilterArray(filters.nominalCodeId));
    return nominalCodeFilterOptions.filter((nominalCode) => selected.has(nominalCode.nominal_code_id));
  }, [nominalCodeFilterOptions, filters.nominalCodeId]);

  const exportCategoryFields = useMemo(
    () => {
      const groupSet = new Set(subcontractedNominalCodes);
      return BASE_EXPORT_CATEGORY_FIELDS.filter((field) => {
        const code = SUBCONTRACTED_KEY_CODE_MAP[field.key];
        if (!code) {
          return true;
        }
        return !groupSet.has(code);
      });
    },
    [subcontractedNominalCodes]
  );
  const nominalCategoryNameMap = useMemo(
    () => buildNominalCategoryNameMap(subcontractedNominalCodes),
    [subcontractedNominalCodes]
  );

  const categoryCodeSets = useMemo(() => {
    const dynamicSets = buildCategoryCodeSetsFromApi(nominalCodes, nominalCategoryNameMap);
    const merged = { ...buildDefaultCategoryCodeSets(subcontractedNominalCodes) };
    Object.entries(dynamicSets).forEach(([key, codes]) => {
      if (Array.isArray(codes) && codes.length) {
        merged[key] = codes;
      }
    });
    return merged;
  }, [nominalCodes, nominalCategoryNameMap, subcontractedNominalCodes]);

  const supplierCategoryMap = useMemo(() => {
    const map = new Map();
    suppliers.forEach((supplier) => {
      if (supplier?.supplier_id) {
        map.set(supplier.supplier_id, supplier.supplier_category_id || '');
      }
    });
    return map;
  }, [suppliers]);

  const supplierStatusMap = useMemo(() => {
    const map = new Map();
    suppliers.forEach((supplier) => {
      if (supplier?.supplier_id) {
        map.set(supplier.supplier_id, supplier.supplier_status_id || '');
      }
    });
    return map;
  }, [suppliers]);

  const supplierDiscountMap = useMemo(() => {
    const map = new Map();
    suppliers.forEach((supplier) => {
      if (supplier?.supplier_id) {
        map.set(supplier.supplier_id, supplier.discount ?? null);
      }
    });
    return map;
  }, [suppliers]);

  const normaliseStatusValues = useCallback((overallValue, approvedValue, notApprovedValue, blockedValue) => {
    const normalisedApprovedValue = toNumber(approvedValue);
    let normalisedNotApprovedValue = toNumber(notApprovedValue);
    const normalisedBlockedValue = toNumber(blockedValue);
    const statusTotal = normalisedApprovedValue + normalisedNotApprovedValue + normalisedBlockedValue;
    if (Math.abs(statusTotal - overallValue) > 0.000001) {
      normalisedNotApprovedValue += overallValue - statusTotal;
    }
    return {
      approvedValue: normalisedApprovedValue,
      notApprovedValue: normalisedNotApprovedValue,
      blockedValue: normalisedBlockedValue,
    };
  }, []);

  const buildStatusTotalsFromSuppliers = useCallback((supplierEntries) => {
    const totals = {
      approvedValue: 0,
      notApprovedValue: 0,
      blockedValue: 0,
    };
    const entries = supplierEntries instanceof Map
      ? Array.from(supplierEntries.values())
      : Array.isArray(supplierEntries)
        ? supplierEntries
        : [];

    entries.forEach((supplierEntry) => {
      const netValue = toNumber(supplierEntry?.invoice) + toNumber(supplierEntry?.credit);
      const supplierStatus = supplierStatusMap.get(supplierEntry?.supplierId) || '';
      if (supplierStatus === 'approved') {
        totals.approvedValue += netValue;
      } else if (NOT_ALLOWED_SUPPLIER_STATUSES.has(supplierStatus)) {
        totals.blockedValue += netValue;
      } else {
        totals.notApprovedValue += netValue;
      }
    });

    return totals;
  }, [supplierStatusMap]);

  const breakdownFieldConfig = useMemo(
    () => {
      const mapped = BASE_BREAKDOWN_FIELD_CONFIG.map((config) => {
        const subcontractedCode = SUBCONTRACTED_KEY_CODE_MAP[config.key];
        const isGroupedSubcontracted =
          subcontractedCode && subcontractedNominalCodes.includes(subcontractedCode);
        return {
          ...config,
          parentKey: subcontractedCode ? (isGroupedSubcontracted ? 'sub_contracted' : null) : config.parentKey,
          codes: categoryCodeSets[config.codeSetKey] || [],
        };
      });

      const standaloneSubKeys = new Set(
        mapped
          .filter(
            (config) =>
              config.key.startsWith('sub_contracted_') &&
              config.key !== 'sub_contracted' &&
              !config.parentKey
          )
          .map((config) => config.key)
      );
      if (!standaloneSubKeys.size) {
        return mapped;
      }

      const reordered = [];
      const added = new Set();
      const standaloneConfigs = mapped.filter((config) => standaloneSubKeys.has(config.key));

      mapped.forEach((config) => {
        if (config.key === 'storage_and_recovery') {
          standaloneConfigs.forEach((standalone) => {
            if (!added.has(standalone.key)) {
              reordered.push(standalone);
              added.add(standalone.key);
            }
          });
          reordered.push(config);
          added.add(config.key);
          return;
        }
        if (standaloneSubKeys.has(config.key)) {
          return;
        }
        reordered.push(config);
        added.add(config.key);
      });

      return reordered;
    },
    [categoryCodeSets, subcontractedNominalCodes]
  );

  const knownCategoryCodes = useMemo(() => {
    const set = new Set();
    breakdownFieldConfig.forEach((config) => {
      (config.codes || []).forEach((code) => set.add(normalizeNominalCodeId(code)));
    });
    return set;
  }, [breakdownFieldConfig]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [locationRes, supplierRes, granularityRes, nominalCodeRes, regionRes] = await Promise.all([
          axiosInstance.get('/api/v1/location'),
          axiosInstance.get('/api/v1/supplier'),
          axiosInstance.get('/api/v1/granularity'),
          axiosInstance.get('/api/v1/nominal-code'),
          axiosInstance.get('/api/v1/region'),
        ]);

        setLocations(locationRes?.data?.data || []);
        setRegions(regionRes?.data?.data || []);
        setSuppliers(supplierRes?.data?.data || []);
        setNominalCodes(nominalCodeRes?.data?.data || []);
        const nominalMeta = nominalCodeRes?.data?.meta || {};
        setSubcontractedNominalCodes(nominalMeta.subcontracted_nominal_codes || []);
        setAdasNominalCode(nominalMeta.adas_nominal_code || '50722070');

        const fetchedGranularities = (granularityRes?.data?.data || []).map((item) => ({
          value: item.granularity_id,
          label: item.name || item.granularity_id,
        }));
        setGranularityOptions(fetchedGranularities);

        if (fetchedGranularities.length > 0) {
          setFilters((prev) => ({
            ...prev,
            granularity:
              prev.granularity && fetchedGranularities.some((g) => g.value === prev.granularity)
                ? prev.granularity
                : fetchedGranularities[0].value,
          }));
        }
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
        setError('Unable to load filter options. Please try again later.');
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchBudgets = async () => {
      try {
        setBudgetsLoading(true);
        const response = await axiosInstance.get('/api/v1/budget');
        if (cancelled) return;
        const documents = response?.data?.data || [];
        const map = new Map();
        documents.forEach((budget) => {
          const locationId = budget?.location_id;
          if (!locationId) return;
          const siteBudget = Number(budget?.site_budget ?? budget?.budget_usage_threshold ?? 0);
          const partsBudget = Number(budget?.parts_budget);
          map.set(locationId, {
            siteBudget: Number.isFinite(siteBudget) ? siteBudget : 0,
            partsBudget: Number.isFinite(partsBudget) ? partsBudget : null,
          });
        });
        setBudgetsByLocation(map);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load budgets:', err);
          setBudgetsByLocation(new Map());
        }
      } finally {
        if (!cancelled) {
          setBudgetsLoading(false);
        }
      }
    };

    fetchBudgets();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    const selectedLocationIds = toFilterArray(filters.locationId);
    const selectedRegionIds = toFilterArray(filters.regionId);
    if (selectedLocationIds.length === 0 || selectedRegionIds.length === 0) return;
    const selectedRegionSet = new Set(selectedRegionIds);
    const validLocationIds = selectedLocationIds.filter((locationId) => {
      const selectedLocation = locations.find((location) => location.location_id === locationId);
      return selectedLocation && selectedRegionSet.has(selectedLocation.region_id);
    });
    if (validLocationIds.length !== selectedLocationIds.length) {
      handleFilterChange('locationId', validLocationIds, { triggerApply: true, usePageSpinner: false });
    }
  }, [filters.locationId, filters.regionId, locations]);

  const handleFilterChange = (field, value, { triggerApply = false, usePageSpinner = false } = {}) => {
    setFilters((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      filtersRef.current = updated;
      return updated;
    });

    if (triggerApply) {
      handleApplyFilters(usePageSpinner);
    }
  };

  const handleDateChange = (field, value) => {
    setQuickRange('custom');
    handleFilterChange(field, value);
  };

  const startLoaders = (usePageSpinner) => {
    if (usePageSpinner) {
      setLoading(true);
      setChartsLoading(false);
    } else {
      setChartsLoading(true);
    }
  };

  const stopLoaders = () => {
    setLoading(false);
    setChartsLoading(false);
  };

  const handleApplyFilters = useCallback(async (usePageSpinner = false) => {
    const currentFilters = filtersRef.current;
    setError('');

    if (activeTab !== 'dashboard') {
      setAppliedFilters(currentFilters);
      setHasAttemptedLoad(true);
      return;
    }

    startLoaders(usePageSpinner);
    try {
      const fallbackGranularity =
        currentFilters.granularity || granularityOptions[0]?.value || 'daily';
      if (currentFilters.granularity !== fallbackGranularity) {
        const nextFilters = { ...currentFilters, granularity: fallbackGranularity };
        filtersRef.current = nextFilters;
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
      }
      const params = {
        granularity: fallbackGranularity,
        start_date: currentFilters.startDate,
        end_date: currentFilters.endDate,
      };

      const regionParam = filterParamValue(currentFilters.regionId);
      if (regionParam) {
        params.region_id = regionParam;
      }
      const locationParam = filterParamValue(currentFilters.locationId);
      if (locationParam) {
        params.location_id = locationParam;
      }
      const supplierParam = filterParamValue(currentFilters.supplierId);
      if (supplierParam) {
        params.supplier_id = supplierParam;
      }
      if (currentFilters.costBy) {
        params.cost_by_address = currentFilters.costBy;
      }
      const nominalCodeParam = filterParamValue(currentFilters.nominalCodeId);
      if (nominalCodeParam) {
        params.nominal_code_id = nominalCodeParam;
      }

      const response = await axiosInstance.get('/api/v1/dashboard', { params });
      setDashboardData(response?.data ?? null);
      setAppliedFilters(currentFilters);
      lastDashboardFiltersRef.current = currentFilters;
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Unable to load dashboard data. Please adjust filters or try again later.');
      setDashboardData(null);
    } finally {
      stopLoaders();
      setHasAttemptedLoad(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    const currentFilters = filtersRef.current;
    const lastFilters = lastDashboardFiltersRef.current;
    const hasChanges = !lastFilters || Object.keys(currentFilters).some(
      (key) => currentFilters[key] !== lastFilters[key]
    );
    if (hasChanges) {
      handleApplyFilters(false);
    }
  }, [activeTab, handleApplyFilters]);

  useEffect(() => {
    if (!optionsLoading && !hasAttemptedLoad) {
      handleApplyFilters(true);
    }
  }, [optionsLoading, hasAttemptedLoad, handleApplyFilters]);

  const handleCostByToggle = (_event, newCostBy) => {
    if (newCostBy === undefined || newCostBy === null) return;
    handleFilterChange('costBy', newCostBy, { triggerApply: true, usePageSpinner: false });
  };

  const handleQuickRangeChange = (event, newRange) => {
    if (!newRange) return;

    if (newRange === 'custom') {
      setQuickRange('custom');
      return;
    }

    const range = getRangeDates(newRange);
    const updatedFilters = {
      ...filtersRef.current,
      startDate: range.start,
      endDate: range.end,
    };

    filtersRef.current = updatedFilters;
    setQuickRange(newRange);
    setFilters(updatedFilters);
    handleApplyFilters(false);
  };

  const handleClear = () => {
    const range = getRangeDates('this_month');
    setQuickRange('this_month');
    setLocationSortMode('cost');
    const resetFilters = {
      regionId: [],
      locationId: [],
      supplierId: [],
      nominalCodeId: [],
      granularity: granularityOptions[0]?.value || '',
      startDate: range.start,
      endDate: range.end,
      costBy: 'repair',
    };
    filtersRef.current = resetFilters;
    setFilters(resetFilters);
    setDashboardData(null);
    setError('');
    setHasAttemptedLoad(false);
    setIsDrilldownVisible(false);
    setSelectedLocationId(null);
    setSelectedCategoryKey(null);
    setSelectedNominalCodeId(null);
  };

  const isApplyDisabled =
    !filters.startDate || !filters.endDate || !filters.granularity || filters.startDate > filters.endDate;

  const extractBreakdownFromNode = useCallback((node) => {
    if (!node) return null;
    const record = {};
    let hasValue = false;
    breakdownFieldConfig.forEach(({ invoiceKey, creditKey }) => {
      const invoiceValue = toNumber(node[invoiceKey]);
      record[invoiceKey] = (record[invoiceKey] || 0) + invoiceValue;
      if (invoiceValue !== 0) {
        hasValue = true;
      }

      const creditValue = toNumber(node[creditKey]);
      record[creditKey] = (record[creditKey] || 0) + creditValue;
      if (creditValue !== 0) {
        hasValue = true;
      }
    });
    breakdownFieldConfig.forEach(({ approvedKey, notApprovedKey, blockedKey }) => {
      if (approvedKey) {
        const approvedValue = toNumber(node[approvedKey]);
        record[approvedKey] = (record[approvedKey] || 0) + approvedValue;
        if (approvedValue !== 0) {
          hasValue = true;
        }
      }
      if (notApprovedKey) {
        const notApprovedValue = toNumber(node[notApprovedKey]);
        record[notApprovedKey] = (record[notApprovedKey] || 0) + notApprovedValue;
        if (notApprovedValue !== 0) {
          hasValue = true;
        }
      }
      if (blockedKey) {
        const blockedValue = toNumber(node[blockedKey]);
        record[blockedKey] = (record[blockedKey] || 0) + blockedValue;
        if (blockedValue !== 0) {
          hasValue = true;
        }
      }
    });
    return record;
  }, [breakdownFieldConfig]);

  const mergeBreakdownRecords = useCallback((target, source) => {
    if (!source) return target ? { ...target } : null;
    const base = target ? { ...target } : {};
    Object.entries(source).forEach(([key, value]) => {
      if (!value) return;
      base[key] = (base[key] || 0) + value;
    });
    return base;
  }, []);

  const buildBreakdownTooltipLines = (record) => {
    return breakdownFieldConfig.flatMap(({ invoiceKey, creditKey, invoiceLabel, creditLabel }) => {
      const invoiceValueRaw = record ? record[invoiceKey] : undefined;
      const creditValueRaw = record ? record[creditKey] : undefined;
      const invoiceValue = Number.isFinite(invoiceValueRaw) ? invoiceValueRaw : 0;
      const creditValue = Number.isFinite(creditValueRaw) ? creditValueRaw : 0;
      return [
        `${invoiceLabel}: ${formatCurrency(invoiceValue)}`,
        `${creditLabel}: ${formatCurrency(creditValue)}`,
      ];
    });
  };

  const aggregateNominalCodesForLocation = useCallback((location) => {
    const result = new Map();

    const processNode = (node) => {
      if (!node || typeof node !== 'object') return;
      if (Array.isArray(node.nominal_codes)) {
        node.nominal_codes.forEach((nominal) => {
          const codeId = normalizeNominalCodeId(nominal?.nominal_code_id);
      const entry = result.get(codeId) || {
        nominalCodeId: codeId,
        invoice: 0,
        credit: 0,
        suppliers: new Map(),
      };
          entry.invoice += toNumber(nominal?.aggregate_invoice_amount);
          entry.credit += toNumber(nominal?.aggregate_credit_note_amount);
          if (Array.isArray(nominal?.suppliers)) {
            nominal.suppliers.forEach((supplier) => {
              const supplierId = supplier?.supplier_id || 'Unspecified Supplier';
              const supplierEntry = entry.suppliers.get(supplierId) || {
                supplierId,
                invoice: 0,
                credit: 0,
                discountAgreed: null,
                discountReceived: null,
                actualTotal: 0,
                retailTotal: 0,
              };
              supplierEntry.invoice += toNumber(supplier?.aggregate_invoice_amount);
              supplierEntry.credit += toNumber(supplier?.aggregate_credit_note_amount);
              supplierEntry.actualTotal += toNumber(supplier?.aggregate_actual_price);
              supplierEntry.retailTotal += toNumber(supplier?.aggregate_retail_price);
              if (supplierEntry.discountAgreed === null || supplierEntry.discountAgreed === undefined) {
                if (supplier?.discount_agreed !== null && supplier?.discount_agreed !== undefined) {
                  supplierEntry.discountAgreed = supplier.discount_agreed;
                }
              }
              entry.suppliers.set(supplierId, supplierEntry);
            });
          }
          result.set(codeId, entry);
        });
      }

      ['days', 'weeks', 'months', 'years'].forEach((field) => {
        if (Array.isArray(node?.[field])) {
          node[field].forEach(processNode);
        }
      });
    };

    processNode(location);
    return result;
  }, []);

  const getTotals = useCallback((item = {}) => {
    const rawInvoice = toNumber(
      item.aggregate_invoice_amount ?? item.invoice ?? item.aggregateInvoiceAmount ?? 0
    );
    const rawCredit = toNumber(
      item.aggregate_credit_note_amount ?? item.credit ?? item.aggregateCreditNoteAmount ?? 0
    );
    const rawNet = toNumber(
      item.overall_net_amount ?? item.net ?? item.overallNetAmount
    );

    const invoice = rawInvoice;
    const credit = rawCredit;
    const total = Number.isFinite(rawNet) && rawNet !== 0 ? rawNet : rawInvoice + rawCredit;

    return { invoice, credit, total };
  }, []);

  const accumulateTotals = (acc, addition) => ({
    invoice: acc.invoice + addition.invoice,
    credit: acc.credit + addition.credit,
    total: acc.total + addition.total,
  });

  const computeSeriesTotals = useCallback(
    (series = []) =>
      series.reduce((acc, point) => accumulateTotals(acc, point), { invoice: 0, credit: 0, total: 0 }),
    []
  );

  const calculateCategoryTotal = useCallback((record, categoryKey) => {
    if (!record) return 0;
    const invoiceKey = `${categoryKey}_invoice_amount`;
    const creditKey = `${categoryKey}_credit_note_amount`;
    return toNumber(record[invoiceKey]) + toNumber(record[creditKey]);
  }, []);

  const showTooltip = useCallback((event, content) => {
    setHoverTooltip({
      visible: true,
      x: event.clientX + 12,
      y: event.clientY - 28,
      content,
    });
  }, []);

  const moveTooltip = useCallback((event) => {
    setHoverTooltip((prev) =>
      prev.visible
        ? {
            ...prev,
            x: event.clientX + 12,
            y: event.clientY - 28,
          }
        : prev
    );
  }, []);

  const hideTooltip = useCallback(() => {
    setHoverTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  }, []);

  const handleSupplierSortChange = useCallback((sortKey) => {
    setSupplierSortConfig((prev) => {
      if (prev.key === sortKey) {
        return { key: sortKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: sortKey, direction: 'desc' };
    });
  }, []);

  const sortSupplierRows = useCallback(
    (rows = [], percentBase = 0) => {
      const normalizedBase = toNumber(percentBase);
      const { key: sortKey, direction } = supplierSortConfig;

      const getValue = (row) => {
        switch (sortKey) {
          case 'supplier':
            return (row.displayName || '').toLowerCase();
          case 'code':
            return (row.supplierId || '').toLowerCase();
          case 'invoice':
            return toNumber(row.invoice);
          case 'credit':
            return toNumber(row.credit);
          case 'percent':
            return normalizedBase !== 0
              ? (toNumber(row.invoice) + toNumber(row.credit)) / normalizedBase
              : 0;
          case 'discountAgreed':
            return row.discountAgreed === null || row.discountAgreed === undefined
              ? -Infinity
              : Number(row.discountAgreed);
          case 'discountReceived':
            return row.discountReceived === null || row.discountReceived === undefined
              ? -Infinity
              : Number(row.discountReceived);
          case 'total':
          default:
            return toNumber(row.invoice) + toNumber(row.credit);
        }
      };

      const sorted = [...rows];
      sorted.sort((a, b) => {
        const valueA = getValue(a);
        const valueB = getValue(b);
        if (typeof valueA === 'string' || typeof valueB === 'string') {
          const comparison = String(valueA).localeCompare(String(valueB));
          return direction === 'asc' ? comparison : -comparison;
        }
        const comparison = valueA - valueB;
        return direction === 'asc' ? comparison : -comparison;
      });
      return sorted;
    },
    [supplierSortConfig]
  );

  const renderSupplierHeaderCell = useCallback(
    (label, sortKey) => (
      <TableCell
        sortDirection={supplierSortConfig.key === sortKey ? supplierSortConfig.direction : false}
        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        <TableSortLabel
          active={supplierSortConfig.key === sortKey}
          direction={supplierSortConfig.key === sortKey ? supplierSortConfig.direction : 'asc'}
          onClick={() => handleSupplierSortChange(sortKey)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    ),
    [supplierSortConfig, handleSupplierSortChange]
  );

  const handleLocationBarClick = useCallback((locationId) => {
    if (!locationId) return;
    setSelectedLocationId(locationId);
    setSelectedCategoryKey(null);
    setSelectedNominalCodeId(null);
    setIsDrilldownVisible(true);
  }, []);

  useEffect(() => {
    if (!isDrilldownVisible) return;
    const frame = requestAnimationFrame(() => {
      if (drilldownRef.current) {
        drilldownRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [isDrilldownVisible, selectedLocationId]);

  const handleCloseDrilldown = useCallback(() => {
    setIsDrilldownVisible(false);
  }, []);

  const handleDrilldownExited = useCallback(() => {
    setSelectedLocationId(null);
    setSelectedCategoryKey(null);
    setSelectedNominalCodeId(null);
  }, []);

  const mapSeriesEntry = (label, totals, breakdown) => ({
    label,
    invoice: totals.invoice,
    credit: totals.credit,
    total: totals.total,
    breakdown: breakdown ? { ...breakdown } : null,
  });

  const ensureLabelSort = (series) => series.sort((a, b) => a.label.localeCompare(b.label));

  const buildSeries = useCallback(
    (location) => {
      const years = location?.years || [];
      const granularity = filters.granularity;

      if (!granularity || !years.length) return [];

      if (granularity === 'yearly') {
        return years.map((year) =>
          mapSeriesEntry(`${year.year}`, getTotals(year), extractBreakdownFromNode(year))
        );
      }

      if (granularity === 'quarterly') {
        const quarterMap = new Map();
        years.forEach((year) => {
          year?.months?.forEach((month) => {
            if (typeof month?.month !== 'number') return;
            const quarter = Math.floor((month.month - 1) / 3) + 1;
            const key = `${year.year} Q${quarter}`;
            const totals = getTotals(month);
            const breakdown = extractBreakdownFromNode(month);
            const existing = quarterMap.get(key);
            if (existing) {
              existing.totals = accumulateTotals(existing.totals, totals);
              existing.breakdown = mergeBreakdownRecords(existing.breakdown, breakdown);
            } else {
              quarterMap.set(key, { totals, breakdown });
            }
          });
        });
        return ensureLabelSort(
          Array.from(quarterMap.entries()).map(([label, value]) =>
            mapSeriesEntry(label, value.totals, value.breakdown)
          )
        );
      }

      if (granularity === 'monthly') {
        const months = [];
        years.forEach((year) => {
          year?.months?.forEach((month) => {
            if (typeof month?.month !== 'number') return;
            months.push(
              mapSeriesEntry(
                `${year.year}-${String(month.month).padStart(2, '0')}`,
                getTotals(month),
                extractBreakdownFromNode(month)
              )
            );
          });
        });
        return ensureLabelSort(months);
      }

      if (granularity === 'weekly') {
        const weekMap = new Map();
        years.forEach((year) => {
          year?.months?.forEach((month) => {
            month?.weeks?.forEach((week) => {
              if (typeof week?.week !== 'number') return;
              const key = `${year.year}-W${String(week.week).padStart(2, '0')}`;
              const totals = getTotals(week);
              const breakdown = extractBreakdownFromNode(week);
              const existing = weekMap.get(key);
              if (existing) {
                existing.totals = accumulateTotals(existing.totals, totals);
                existing.breakdown = mergeBreakdownRecords(existing.breakdown, breakdown);
              } else {
                weekMap.set(key, { totals, breakdown });
              }
            });
          });
        });
        return ensureLabelSort(
          Array.from(weekMap.entries()).map(([label, value]) =>
            mapSeriesEntry(label, value.totals, value.breakdown)
          )
        );
      }

      const days = [];
      years.forEach((year) => {
        year?.months?.forEach((month) => {
          month?.weeks?.forEach((week) => {
            week?.days?.forEach((day) => {
              if (typeof day?.day !== 'number') return;
              const label = `${year.year}-${String(month.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
              days.push(mapSeriesEntry(label, getTotals(day), extractBreakdownFromNode(day)));
            });
          });
        });
      });
      return ensureLabelSort(days);
    },
    [appliedFilters.granularity, getTotals, extractBreakdownFromNode, mergeBreakdownRecords]
  );

  const locationNodes = useMemo(() => {
    const segments = Array.isArray(dashboardData?.data)
      ? dashboardData.data
      : Array.isArray(dashboardData)
      ? dashboardData
      : [];
    return segments.flatMap((segment) => segment.locations || []);
  }, [dashboardData]);

  const locationDataMap = useMemo(() => {
    const map = new Map();
    locationNodes.forEach((location) => {
      map.set(location.location_id, location);
    });
    return map;
  }, [locationNodes]);

  const locationCharts = useMemo(() => {
    if (!locationNodes.length) return [];
    return locationNodes
      .map((location) => ({
        locationId: location.location_id,
        series: buildSeries(location),
      }))
      .filter((chart) => chart.series.length > 0);
  }, [locationNodes, buildSeries]);

  const locationSummaries = useMemo(() => {
    return locationCharts.map((chart) => ({
      locationId: chart.locationId,
      ...computeSeriesTotals(chart.series),
      breakdown: extractBreakdownFromNode(locationDataMap.get(chart.locationId)),
    }));
  }, [locationCharts, computeSeriesTotals, locationDataMap, extractBreakdownFromNode]);

  const locationTotalsMap = useMemo(() => {
    const map = new Map();
    locationSummaries.forEach((item) => {
      map.set(item.locationId, item.total);
    });
    return map;
  }, [locationSummaries]);

  const aggregatedNominalData = useMemo(() => {
    if (!selectedLocationId) return null;
    const location = locationDataMap.get(selectedLocationId);
    if (!location) return null;
    return aggregateNominalCodesForLocation(location);
  }, [selectedLocationId, locationDataMap, aggregateNominalCodesForLocation]);

  const selectedLocationBreakdown = useMemo(() => {
    if (!selectedLocationId) return null;
    const location = locationDataMap.get(selectedLocationId);
    if (!location) return null;
    return extractBreakdownFromNode(location) || {};
  }, [selectedLocationId, locationDataMap, extractBreakdownFromNode]);

  const aggregatedBreakdownRecord = useMemo(() => {
    if (!locationNodes.length) return null;
    return locationNodes.reduce(
      (acc, location) => mergeBreakdownRecords(acc, extractBreakdownFromNode(location)),
      null
    );
  }, [locationNodes, mergeBreakdownRecords, extractBreakdownFromNode]);

  const aggregatedNetTotal = useMemo(
    () =>
      locationNodes.reduce(
        (sum, location) =>
          sum + toNumber(location.aggregate_invoice_amount) + toNumber(location.aggregate_credit_note_amount),
        0
      ),
    [locationNodes]
  );

  const aggregatedNominalDataAll = useMemo(() => {
    if (!locationNodes.length) return null;
    const result = new Map();
    locationNodes.forEach((location) => {
      const map = aggregateNominalCodesForLocation(location);
      if (!map) return;
      map.forEach((entry, codeId) => {
        const normalizedCodeId = normalizeNominalCodeId(codeId);
        const existing = result.get(normalizedCodeId);
        if (existing) {
          existing.invoice += toNumber(entry.invoice);
          existing.credit += toNumber(entry.credit);
        } else {
          result.set(normalizedCodeId, {
            nominalCodeId: normalizedCodeId,
            invoice: toNumber(entry.invoice),
            credit: toNumber(entry.credit),
            suppliers: new Map(),
          });
        }
        const target = result.get(normalizedCodeId);
        if (entry.suppliers instanceof Map) {
          entry.suppliers.forEach((supplierEntry, supplierId) => {
            const existingSupplier = target.suppliers.get(supplierId) || {
              supplierId,
              invoice: 0,
              credit: 0,
              discountAgreed: null,
              discountReceived: null,
              actualTotal: 0,
              retailTotal: 0,
            };
            existingSupplier.invoice += toNumber(supplierEntry.invoice);
            existingSupplier.credit += toNumber(supplierEntry.credit);
            existingSupplier.actualTotal += toNumber(supplierEntry.actualTotal);
            existingSupplier.retailTotal += toNumber(supplierEntry.retailTotal);
            if (existingSupplier.discountAgreed === null || existingSupplier.discountAgreed === undefined) {
              if (supplierEntry.discountAgreed !== null && supplierEntry.discountAgreed !== undefined) {
                existingSupplier.discountAgreed = supplierEntry.discountAgreed;
              }
            }
            target.suppliers.set(supplierId, existingSupplier);
          });
        }
      });
    });
    return result;
  }, [locationNodes, aggregateNominalCodesForLocation]);

  useEffect(() => {
    setGlobalCategoryKey(null);
    setGlobalNominalCodeId(null);
  }, [aggregatedBreakdownRecord]);

  const orderedLocationSummaries = useMemo(() => {
    const entries = [...locationSummaries];
    if (locationSortMode === 'alpha') {
      entries.sort((a, b) => {
        const nameA = (locationNameMap.get(a.locationId) || a.locationId).toLowerCase();
        const nameB = (locationNameMap.get(b.locationId) || b.locationId).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else {
      entries.sort(
        (a, b) => (locationTotalsMap.get(b.locationId) || 0) - (locationTotalsMap.get(a.locationId) || 0)
      );
    }
    return entries;
  }, [locationSummaries, locationSortMode, locationNameMap, locationTotalsMap]);

  const buildCostBreakdownExportRows = useCallback(
    (breakdownRecord, panelNominalData, appliedNominalCodeFilter, approvalMode) => {
      const mergeNominalEntries = (existing, addition) => {
        if (!addition) return existing;
        if (!existing) {
          return {
            invoice: toNumber(addition.invoice),
            credit: toNumber(addition.credit),
            suppliers: addition.suppliers instanceof Map ? new Map(addition.suppliers) : new Map(),
          };
        }
        existing.invoice += toNumber(addition.invoice);
        existing.credit += toNumber(addition.credit);
        if (addition.suppliers instanceof Map) {
          addition.suppliers.forEach((supplierEntry, supplierId) => {
            const current = existing.suppliers.get(supplierId) || {
              supplierId,
              invoice: 0,
              credit: 0,
              discountAgreed: null,
              discountReceived: null,
              actualTotal: 0,
              retailTotal: 0,
            };
            current.invoice += toNumber(supplierEntry.invoice);
            current.credit += toNumber(supplierEntry.credit);
            current.actualTotal += toNumber(supplierEntry.actualTotal);
            current.retailTotal += toNumber(supplierEntry.retailTotal);
            if (current.discountAgreed === null || current.discountAgreed === undefined) {
              if (supplierEntry.discountAgreed !== null && supplierEntry.discountAgreed !== undefined) {
                current.discountAgreed = supplierEntry.discountAgreed;
              }
            }
            existing.suppliers.set(supplierId, current);
          });
        }
        return existing;
      };

      let otherNominalEntry = null;
      if (panelNominalData instanceof Map) {
        panelNominalData.forEach((entry, codeId) => {
          if (!entry) return;
          if (codeId === UNKNOWN_NOMINAL_CODE) {
            otherNominalEntry = mergeNominalEntries(otherNominalEntry, entry);
            return;
          }
          if (!knownCategoryCodes.has(codeId)) {
            otherNominalEntry = mergeNominalEntries(otherNominalEntry, entry);
          }
        });
      }

      const categoryRows = breakdownFieldConfig.map((config) => {
        const invoiceValue = toNumber(breakdownRecord?.[config.invoiceKey]);
        const creditValue = toNumber(breakdownRecord?.[config.creditKey]);
        const overallValue = invoiceValue + creditValue;
        const { approvedValue, notApprovedValue, blockedValue } = normaliseStatusValues(
          overallValue,
          config.approvedKey ? breakdownRecord?.[config.approvedKey] : 0,
          config.notApprovedKey ? breakdownRecord?.[config.notApprovedKey] : 0,
          config.blockedKey ? breakdownRecord?.[config.blockedKey] : 0
        );
        return {
          key: config.key,
          title: config.title,
          invoiceValue,
          creditValue,
          approvedValue,
          notApprovedValue,
          blockedValue,
          parentKey: config.parentKey || null,
        };
      });

      if (
        otherNominalEntry &&
        (toNumber(otherNominalEntry.invoice) !== 0 ||
          toNumber(otherNominalEntry.credit) !== 0 ||
          (otherNominalEntry.suppliers && otherNominalEntry.suppliers.size > 0))
      ) {
        const overallValue = toNumber(otherNominalEntry.invoice) + toNumber(otherNominalEntry.credit);
        const statusTotals = buildStatusTotalsFromSuppliers(otherNominalEntry.suppliers);
        const { approvedValue, notApprovedValue, blockedValue } = normaliseStatusValues(
          overallValue,
          statusTotals.approvedValue,
          statusTotals.notApprovedValue,
          statusTotals.blockedValue
        );
        categoryRows.push({
          key: 'unknown_nominal_codes',
          title: 'Other',
          invoiceValue: toNumber(otherNominalEntry.invoice),
          creditValue: toNumber(otherNominalEntry.credit),
          approvedValue,
          notApprovedValue,
          blockedValue,
          parentKey: null,
        });
      }

      let displayedCategoryRows = categoryRows;
      if (appliedNominalCodeFilter) {
        const normalizedFilter = normalizeNominalCodeId(appliedNominalCodeFilter);
        const isCarPartsFilter = (categoryCodeSets.car_parts || []).some(
          (code) => normalizeNominalCodeId(code) === normalizedFilter
        );
        const matchesFilter = breakdownFieldConfig.some((config) =>
          (config.codes || []).some((code) => normalizeNominalCodeId(code) === normalizedFilter)
        );
        if (matchesFilter && !isCarPartsFilter) {
          const filteredNominalEntry = panelNominalData?.get(normalizedFilter);
          const matchingConfigs = breakdownFieldConfig.filter((config) =>
            (config.codes || []).some((code) => normalizeNominalCodeId(code) === normalizedFilter)
          );
          const matchedConfig =
            matchingConfigs.find((config) => config.key !== 'sub_contracted') || matchingConfigs[0] || null;
          const matchedCategoryRow = matchedConfig
            ? categoryRows.find((row) => row.key === matchedConfig.key)
            : null;
          const resolvedCode = normalizedFilter === UNKNOWN_NOMINAL_CODE ? '' : normalizedFilter;
          const nominalMeta = nominalCodes.find((nominal) => nominal.nominal_code_id === resolvedCode);
          const filteredRowTitle = nominalMeta?.name
            ? nominalMeta.name
            : formatNominalCodeLabel(normalizedFilter);

          if (matchedCategoryRow) {
            displayedCategoryRows = [
              {
                ...matchedCategoryRow,
                title: filteredRowTitle,
                parentKey: null,
              },
            ];
        } else {
          const filteredInvoice = toNumber(filteredNominalEntry?.invoice);
          const filteredCredit = toNumber(filteredNominalEntry?.credit);
          const overallValue = filteredInvoice + filteredCredit;
          const statusTotals = buildStatusTotalsFromSuppliers(filteredNominalEntry?.suppliers);
          const { approvedValue, notApprovedValue, blockedValue } = normaliseStatusValues(
            overallValue,
            statusTotals.approvedValue,
            statusTotals.notApprovedValue,
            statusTotals.blockedValue
          );
          displayedCategoryRows = [
            {
              key: `filtered_nominal_${normalizedFilter}`,
              title: filteredRowTitle,
              invoiceValue: filteredInvoice,
              creditValue: filteredCredit,
              approvedValue,
              notApprovedValue,
              blockedValue,
              parentKey: null,
            },
          ];
        }
        }
        if (matchesFilter && isCarPartsFilter) {
          displayedCategoryRows = categoryRows.filter(
            (row) => row.key === 'car_parts' || row.parentKey === 'car_parts'
          );
        }
      }

      const percentBase = displayedCategoryRows.reduce(
        (sum, row) => (row.parentKey ? sum : sum + row.invoiceValue + row.creditValue),
        0
      );

      const calcPercent = (value) =>
        percentBase && Number.isFinite(percentBase) && Math.abs(percentBase) > 0.000001
          ? (value / percentBase) * 100
          : null;

      const resolveApprovalPercentBase = (row) => {
        if (approvalMode !== 'split') return percentBase;
        if (!row.parentKey) return percentBase;
        const rowTotal = row.approvedValue + row.notApprovedValue + row.blockedValue;
        return rowTotal || percentBase;
      };

      const rows = displayedCategoryRows.map((row) => {
        const approvalBase = resolveApprovalPercentBase(row);
        const approvedPercent = approvalBase ? (row.approvedValue / approvalBase) * 100 : null;
        const notApprovedPercent = approvalBase ? (row.notApprovedValue / approvalBase) * 100 : null;
        const blockedPercent = approvalBase ? (row.blockedValue / approvalBase) * 100 : null;
        const approvedRounded = Number.isFinite(approvedPercent) ? Number(approvedPercent.toFixed(2)) : null;
        const notApprovedRounded = Number.isFinite(notApprovedPercent) ? Number(notApprovedPercent.toFixed(2)) : null;
        const blockedRounded = Number.isFinite(blockedPercent) ? Number(blockedPercent.toFixed(2)) : null;
        const shareRounded =
          Number.isFinite(approvedRounded) &&
          Number.isFinite(notApprovedRounded) &&
          Number.isFinite(blockedRounded)
            ? approvedRounded + notApprovedRounded + blockedRounded
            : null;
        return [
          row.title,
          formatCurrency(row.invoiceValue),
          formatCurrency(row.creditValue),
          formatCurrency(row.invoiceValue + row.creditValue),
          formatCurrency(row.approvedValue),
          formatCurrency(row.notApprovedValue),
          formatCurrency(row.blockedValue),
          Number.isFinite(approvedRounded) ? `${approvedRounded.toFixed(2)}%` : '-',
          Number.isFinite(notApprovedRounded) ? `${notApprovedRounded.toFixed(2)}%` : '-',
          Number.isFinite(blockedRounded) ? `${blockedRounded.toFixed(2)}%` : '-',
          Number.isFinite(shareRounded) ? `${shareRounded.toFixed(2)}%` : '-',
        ];
      });

      const totalRow = [
        'Total',
        formatCurrency(
          displayedCategoryRows.reduce((sum, row) => (row.parentKey ? sum : sum + row.invoiceValue), 0)
        ),
        formatCurrency(
          displayedCategoryRows.reduce((sum, row) => (row.parentKey ? sum : sum + row.creditValue), 0)
        ),
        formatCurrency(
          displayedCategoryRows.reduce((sum, row) => (
            row.parentKey ? sum : sum + row.invoiceValue + row.creditValue
          ), 0)
        ),
        formatCurrency(
          displayedCategoryRows.reduce((sum, row) => (row.parentKey ? sum : sum + row.approvedValue), 0)
        ),
        formatCurrency(
          displayedCategoryRows.reduce((sum, row) => (row.parentKey ? sum : sum + row.notApprovedValue), 0)
        ),
        formatCurrency(
          displayedCategoryRows.reduce((sum, row) => (row.parentKey ? sum : sum + row.blockedValue), 0)
        ),
          (() => {
            const approvedTotal = displayedCategoryRows.reduce(
              (sum, row) => (row.parentKey ? sum : sum + row.approvedValue),
              0
            );
            const notApprovedTotal = displayedCategoryRows.reduce(
              (sum, row) => (row.parentKey ? sum : sum + row.notApprovedValue),
              0
            );
            const blockedTotal = displayedCategoryRows.reduce(
              (sum, row) => (row.parentKey ? sum : sum + row.blockedValue),
              0
            );
            const approvedPercent = calcPercent(approvedTotal);
            const notApprovedPercent = calcPercent(notApprovedTotal);
            const blockedPercent = calcPercent(blockedTotal);
            const approvedRounded = Number.isFinite(approvedPercent) ? Number(approvedPercent.toFixed(2)) : null;
            const notApprovedRounded = Number.isFinite(notApprovedPercent) ? Number(notApprovedPercent.toFixed(2)) : null;
            const blockedRounded = Number.isFinite(blockedPercent) ? Number(blockedPercent.toFixed(2)) : null;
            const shareRounded =
              Number.isFinite(approvedRounded) &&
              Number.isFinite(notApprovedRounded) &&
              Number.isFinite(blockedRounded)
                ? approvedRounded + notApprovedRounded + blockedRounded
                : null;
          return [
            Number.isFinite(approvedRounded) ? `${approvedRounded.toFixed(2)}%` : '-',
            Number.isFinite(notApprovedRounded) ? `${notApprovedRounded.toFixed(2)}%` : '-',
            Number.isFinite(blockedRounded) ? `${blockedRounded.toFixed(2)}%` : '-',
            Number.isFinite(shareRounded) ? `${shareRounded.toFixed(2)}%` : '-',
          ];
        })(),
      ].flat();

      return { rows, totalRow };
    },
    [
      breakdownFieldConfig,
      buildStatusTotalsFromSuppliers,
      categoryCodeSets,
      knownCategoryCodes,
      nominalCodes,
      normaliseStatusValues,
    ]
  );

  const handleExportAllLocationsCostBreakdown = useCallback(() => {
    if (!orderedLocationSummaries.length) return;
    setExportingBreakdown(true);
    try {
      const headers = [
        'Site',
        'Region',
        'Category',
        'Invoice Amount (Net)',
        'Credit Amount (Net)',
        'Overall Amount (Net)',
        'Approved (Net)',
        'Not Approved (Net)',
        'Not Allowed (Net)',
        'Approved Total Spend (%)',
        'Not Approved Total Spend (%)',
        'Not Allowed Total Spend (%)',
        'Share of Total Spend (%)',
      ];

      const rows = [];
      const appliedNominalCodeFilter = (toFilterArray(appliedFilters.nominalCodeId).length === 1 ? toFilterArray(appliedFilters.nominalCodeId)[0] : '');

      orderedLocationSummaries.forEach((summary) => {
        const location = locationDataMap.get(summary.locationId);
        const breakdown = summary.breakdown || {};
        const panelNominalData = location ? aggregateNominalCodesForLocation(location) : null;
        const { rows: categoryRows, totalRow } = buildCostBreakdownExportRows(
          breakdown,
          panelNominalData,
          appliedNominalCodeFilter,
          approvalPercentMode
        );
        const locationName = locationNameMap.get(summary.locationId) || summary.locationId;
        const regionName = locationRegionNameMap.get(summary.locationId) || '';

        categoryRows.forEach((row) => {
          rows.push([locationName, regionName, ...row]);
        });
        rows.push([locationName, regionName, ...totalRow]);
      });

      if (aggregatedBreakdownRecord) {
        const { rows: aggregateRows, totalRow: aggregateTotal } = buildCostBreakdownExportRows(
          aggregatedBreakdownRecord,
          aggregatedNominalDataAll,
          appliedNominalCodeFilter,
          approvalPercentMode
        );
        aggregateRows.forEach((row) => {
          rows.push(['All Sites', '', ...row]);
        });
        rows.push(['All Sites', '', ...aggregateTotal]);
      }

      const csvContent = [headers, ...rows]
        .map((row) => row.map(escapeCsvValue).join(','))
        .join('\n');

    const BOM = '\uFEFF';

    const blob = new Blob([BOM, csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const rangeLabel = `${filters.startDate || 'start'}-to-${filters.endDate || 'end'}`;
    link.href = url;
    link.download = `cost-breakdown-all-locations-${rangeLabel}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export cost breakdown:', error);
    } finally {
      setExportingBreakdown(false);
    }
  }, [
    orderedLocationSummaries,
    locationNameMap,
    regionNameMap,
    locationRegionNameMap,
    locationDataMap,
    aggregateNominalCodesForLocation,
    aggregatedBreakdownRecord,
    aggregatedNominalDataAll,
    buildCostBreakdownExportRows,
    filters.startDate,
    filters.endDate,
    appliedFilters.nominalCodeId,
    approvalPercentMode,
  ]);

  const handleExportSingleLocationCostBreakdown = useCallback(
    (locationId, locationName) => {
      if (!locationId) return;
      const location = locationDataMap.get(locationId);
      if (!location) return;
      setExportingBreakdown(true);
      try {
        const headers = [
          'Site',
          'Region',
          'Category',
          'Invoice Amount (Net)',
          'Credit Amount (Net)',
          'Overall Amount (Net)',
          'Approved (Net)',
          'Not Approved (Net)',
          'Not Allowed (Net)',
          'Approved Total Spend (%)',
          'Not Approved Total Spend (%)',
          'Not Allowed Total Spend (%)',
          'Share of Total Spend (%)',
        ];
        const breakdown = extractBreakdownFromNode(location) || {};
        const panelNominalData = aggregateNominalCodesForLocation(location);
        const appliedNominalCodeFilter = (toFilterArray(appliedFilters.nominalCodeId).length === 1 ? toFilterArray(appliedFilters.nominalCodeId)[0] : '');
        const { rows: categoryRows, totalRow } = buildCostBreakdownExportRows(
          breakdown,
          panelNominalData,
          appliedNominalCodeFilter,
          approvalPercentMode
        );
        const regionName = locationRegionNameMap.get(locationId) || '';

        const rows = [];
        categoryRows.forEach((row) => rows.push([locationName, regionName, ...row]));
        rows.push([locationName, regionName, ...totalRow]);

        const csvContent = [headers, ...rows]
          .map((row) => row.map(escapeCsvValue).join(','))
          .join('\n');

        const BOM = '\uFEFF';
        const blob = new Blob([BOM, csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const rangeLabel = `${filters.startDate || 'start'}-to-${filters.endDate || 'end'}`;
        link.href = url;
        link.download = `cost-breakdown-${locationName}-${rangeLabel}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to export cost breakdown:', error);
      } finally {
        setExportingBreakdown(false);
      }
    },
    [
      locationDataMap,
      extractBreakdownFromNode,
      aggregateNominalCodesForLocation,
      buildCostBreakdownExportRows,
      regionNameMap,
      locationRegionNameMap,
      filters.startDate,
      filters.endDate,
      appliedFilters.nominalCodeId,
      approvalPercentMode,
    ]
  );

  const displayedLocationCharts = useMemo(() => {
    const charts = [...locationCharts];
    if (locationSortMode === 'alpha') {
      charts.sort((a, b) => {
        const nameA = (locationNameMap.get(a.locationId) || a.locationId).toLowerCase();
        const nameB = (locationNameMap.get(b.locationId) || b.locationId).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else {
      charts.sort(
        (a, b) => (locationTotalsMap.get(b.locationId) || 0) - (locationTotalsMap.get(a.locationId) || 0)
      );
    }
    return charts;
  }, [locationCharts, locationSortMode, locationNameMap, locationTotalsMap]);

  const locationColorMap = useMemo(() => {
    const map = new Map();
    let colorIndex = 0;
    locationSummaries.forEach((item) => {
      if (!map.has(item.locationId)) {
        map.set(item.locationId, CHART_COLORS[colorIndex % CHART_COLORS.length]);
        colorIndex += 1;
      }
    });
    return map;
  }, [locationSummaries]);

  const chartPaperSx = useMemo(
    () => ({
      p: 1.5,
      mb: 3,
      width: '100%'
    }),
    []
  );

  useEffect(() => {
    const measureWidth = () => {
      const element = filterCardRef.current;
      if (!element) return;

      let paddingLeft = 0;
      let paddingRight = 0;
      if (typeof window !== 'undefined') {
        const computed = window.getComputedStyle(element);
        paddingLeft = parseFloat(computed.paddingLeft) || 0;
        paddingRight = parseFloat(computed.paddingRight) || 0;
      }

      const rectWidth = element.getBoundingClientRect().width;
      const contentWidth = Math.max(rectWidth - paddingLeft - paddingRight, 0);
      setFilterCardWidth(contentWidth);
      setFilterCardOuterWidth(rectWidth);
    };

    const element = filterCardRef.current;
    if (!element) {
      measureWidth();
      return undefined;
    }

    measureWidth();

    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      const observer = new ResizeObserver(measureWidth);
      observer.observe(element);
      return () => observer.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measureWidth);
      return () => window.removeEventListener('resize', measureWidth);
    }

    return undefined;
  }, []);

  const formatSeriesLabelForTooltip = useCallback(
    (rawLabel) => {
      const granularity = filters.granularity;
      if (!rawLabel) return '';

      const toMonthYear = (year, month) => {
        const parsedYear = Number(year);
        const parsedMonth = Number(month);
        if (!Number.isFinite(parsedYear) || !Number.isFinite(parsedMonth)) return rawLabel;
        const date = new Date(Date.UTC(parsedYear, parsedMonth - 1, 1));
        return date.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
      };

      const toDayMonthYear = (year, month, day) => {
        const parsedYear = Number(year);
        const parsedMonth = Number(month);
        const parsedDay = Number(day);
        if (!Number.isFinite(parsedYear) || !Number.isFinite(parsedMonth) || !Number.isFinite(parsedDay)) {
          return rawLabel;
        }
        const date = new Date(Date.UTC(parsedYear, parsedMonth - 1, parsedDay));
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      };

      switch (granularity) {
        case 'yearly':
          return `Year ${rawLabel}`;
        case 'quarterly': {
          const [year, quarter] = rawLabel.split(' ');
          return quarter && year ? `${quarter} ${year}` : rawLabel;
        }
        case 'monthly': {
          const [year, month] = rawLabel.split('-');
          return toMonthYear(year, month);
        }
        case 'weekly': {
          const [year, weekPart] = rawLabel.split('-W');
          if (!year || !weekPart) return rawLabel;
          return `Week ${Number(weekPart)} ${year}`;
        }
        case 'daily':
        default: {
          const parts = rawLabel.split('-');
          if (parts.length === 3) {
            return toDayMonthYear(parts[0], parts[1], parts[2]);
          }
          if (parts.length === 2) {
            return toMonthYear(parts[0], parts[1]);
          }
          return rawLabel;
        }
      }
    },
    [filters.granularity]
  );

  const renderHistogram = (chart) => {
    const { locationId, series } = chart;
    if (!series.length) return null;

    const totals = series.map((item) => Math.abs(item.total));
    const maxTotal = Math.max(...totals, 0) || 1;
    const chartHeight = 240;
    const topPadding = 70;
    const bottomPadding = 120;
    const barWidth = 36;
    const barGap = 22;
    const rightPadding = barGap + 48;
    const preferredWidth = filterCardWidth || CHART_CARD_MIN_WIDTH;
    const svgWidth = Math.max(
      HISTOGRAM_LEFT_MARGIN + series.length * (barWidth + barGap) + rightPadding,
      preferredWidth
    );
    const svgHeight = chartHeight + topPadding + bottomPadding;
    const zeroY = chartHeight + topPadding;

    const barColor = locationColorMap.get(locationId) || CHART_COLORS[0];
    const locationTotal = formatCurrency(locationTotalsMap.get(locationId) || 0);

    return (
      <Paper key={locationId} sx={chartPaperSx}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {`${locationNameMap.get(locationId) || locationId} — ${locationTotal}`}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <svg width={svgWidth} height={svgHeight}>
            {series.map((item, index) => {
              const barX = HISTOGRAM_LEFT_MARGIN + index * (barWidth + barGap);
              const barHeight = (Math.abs(item.total) / maxTotal) * chartHeight;
              const y = zeroY - barHeight;
              const periodLabel = formatSeriesLabelForTooltip(item.label);
              const header = periodLabel;
              const baseLines = [
                `Overall Net Amount: ${formatCurrency(item.total)}`,
                `Aggregate Invoice Amount: ${formatCurrency(item.invoice)}`,
                `Aggregate Credit Note Amount: ${formatCurrency(item.credit)}`,
              ];
              const tooltipContent = {
                header,
                lines: baseLines,
              };
              const costLabelY = Math.max(y - 30, 16);
              const axisLabelY = zeroY + bottomPadding - 80;

              return (
                <g
                  key={item.label}
                  onMouseEnter={(event) => showTooltip(event, tooltipContent)}
                  onMouseMove={moveTooltip}
                  onMouseLeave={hideTooltip}
                >
                  <rect
                    x={barX}
                    y={barHeight === 0 ? zeroY - 1 : y}
                    width={barWidth}
                    height={Math.max(barHeight, 1)}
                    fill={barColor}
                    rx={4}
                    ry={4}
                  />
                  <text
                    x={barX + barWidth / 2 - 6}
                    y={y - 35}
                    textAnchor="end"
                    fontSize={CHART_FONT_SIZES.axis}
                    fill="#424242"
                    transform={`rotate(-45 ${barX + barWidth / 2 - 6} ${y - 90})`}
                  >
                    {formatCurrency(item.total)}
                  </text>
                  <text
                    x={barX + barWidth / 2}
                    y={axisLabelY}
                    textAnchor="end"
                    fontSize={CHART_FONT_SIZES.axis}
                    fill="#616161"
                    transform={`rotate(-45 ${barX + barWidth / 2} ${axisLabelY})`}
                  >
                    {item.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </Box>
      </Paper>
    );
  };
  const renderLocationSummaryHistogram = (summaries) => {
    if (!summaries.length) return null;

    const totals = summaries.map((item) => Math.abs(item.total));
    const maxTotal = Math.max(...totals, 0) || 1;
    const chartHeight = 240;
    const topPadding = 120;
    const bottomPadding = 120;
    const barWidth = 42;
    const barGap = 24;
    const rightPadding = barGap + 72;
    const preferredWidth = filterCardWidth || CHART_CARD_MIN_WIDTH;
    const svgWidth = Math.max(
      HISTOGRAM_LEFT_MARGIN + summaries.length * (barWidth + barGap) + rightPadding,
      preferredWidth
    );
    const svgHeight = chartHeight + topPadding + bottomPadding;
    const zeroY = chartHeight + topPadding;

    return (
      <Paper sx={chartPaperSx}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Overall Net Amount by Site
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <svg width={svgWidth} height={svgHeight}>
            {summaries.map((item, index) => {
              const barX = HISTOGRAM_LEFT_MARGIN + index * (barWidth + barGap);
              const barHeight = (Math.abs(item.total) / maxTotal) * chartHeight;
              const y = zeroY - barHeight;
              const color = locationColorMap.get(item.locationId) || CHART_COLORS[index % CHART_COLORS.length];
              const name = locationNameMap.get(item.locationId) || item.locationId;
              const formattedTotal = formatCurrency(item.total);
              const header = name;
              const baseLines = [
                `Overall Net Amount: ${formattedTotal}`,
                `Aggregate Invoice Amount: ${formatCurrency(item.invoice)}`,
                `Aggregate Credit Note Amount: ${formatCurrency(item.credit)}`,
              ];
              const tooltipContent = {
                header,
                lines: baseLines,
              };
              const costLabelY = Math.max(y - 18, 16);
              const axisLabelY = zeroY + bottomPadding - 80;

              return (
                <g
                  key={item.locationId}
                  onMouseEnter={(event) => showTooltip(event, tooltipContent)}
                  onMouseMove={moveTooltip}
                  onMouseLeave={hideTooltip}
                  onClick={() => handleLocationBarClick(item.locationId)}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    x={barX}
                    y={barHeight === 0 ? zeroY - 1 : y}
                    width={barWidth}
                    height={Math.max(barHeight, 1)}
                    fill={color}
                    rx={4}
                    ry={4}
                  />
                  <text
                    x={barX + barWidth / 2}
                    y={y - 35}
                    textAnchor="end"
                    fontSize={CHART_FONT_SIZES.axis}
                    fill="#424242"
                    transform={`rotate(-45 ${barX + barWidth / 2} ${y - 90})`}
                  >
                    {formattedTotal}
                  </text>
                  <text
                    x={barX + barWidth / 2}
                    y={axisLabelY}
                    textAnchor="end"
                    fontSize={CHART_FONT_SIZES.axis}
                    fill="#616161"
                    transform={`rotate(-45 ${barX + barWidth / 2} ${axisLabelY})`}
                  >
                    {name}
                  </text>
                </g>
              );
            })}
          </svg>
        </Box>
      </Paper>
    );
  };

  const renderLocationPieChart = (summaries) => {
    if (!summaries.length) return null;

    const positiveSummaries = summaries.filter((item) => Number.isFinite(item.total) && item.total !== 0);
    if (!positiveSummaries.length) return null;

    const totalSum = positiveSummaries.reduce((acc, item) => acc + Math.abs(item.total), 0) || 1;
    const radius = 160;
    const center = radius + 32;
    let startAngle = 0;

    return (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <svg width={(center + 16) * 2} height={(center + 16) * 2}>
          {positiveSummaries.map((item, index) => {
            const value = Math.abs(item.total);
            const angle = (value / totalSum) * 360;
            const endAngle = startAngle + angle;
            const path = describeArc(center, center, radius, startAngle, endAngle);
            startAngle = endAngle;
            const color =
              locationColorMap.get(item.locationId) || CHART_COLORS[index % CHART_COLORS.length];
            return <path key={`${item.locationId}-${index}`} d={path} fill={color} stroke="#fff" strokeWidth={2} />;
          })}
        </svg>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Share of Overall Net Amount by Location
          </Typography>
          {positiveSummaries.map((item, index) => {
            const color =
              locationColorMap.get(item.locationId) || CHART_COLORS[index % CHART_COLORS.length];
            const name = locationNameMap.get(item.locationId) || item.locationId;
            return (
              <Box key={item.locationId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 14, height: 14, bgcolor: color, borderRadius: '3px' }} />
                <Typography variant="body2" sx={{ fontSize: `${CHART_FONT_SIZES.legend}px` }}>
                  {`${name} — Overall Net ${formatCurrency(item.total)}`}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const describeArc = (centerX, centerY, radius, startAngle, endAngle) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return [
      'M',
      start.x,
      start.y,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      'L',
      centerX,
      centerY,
      'Z',
    ].join(' ');
  };

  const renderCostBreakdownPanel = ({
    locationName,
    breakdownRecord,
    aggregatedNominalData: panelNominalData,
    locationNetTotal,
    selectedCategoryKey: panelSelectedCategoryKey,
    setSelectedCategoryKey: updateCategoryKey,
    selectedNominalCodeId: panelSelectedNominalCodeId,
    setSelectedNominalCodeId: updateNominalCodeId,
    nominalTableRef,
    supplierTableRef,
    panelRef,
    onClose,
    appliedNominalCodeFilter,
    showCloseButton = true,
    extraContent = null,
    panelWidth,
    approvalPercentMode,
    onApprovalPercentModeChange,
  }) => {
    if (!breakdownRecord) return null;
    const formatPercentValue = (numerator, denominator) =>
      denominator ? (numerator / denominator) * 100 : 0;
    const formatPercent = (numerator, denominator) =>
      `${formatPercentValue(numerator, denominator).toFixed(2)}%`;
    const selectedNominalDisplay = panelSelectedNominalCodeId
      ? formatNominalCodeLabel(panelSelectedNominalCodeId)
      : '';

    const mergeNominalEntries = (target, addition) => {
      if (!addition) return target;
      if (!target) {
        target = {
          invoice: 0,
          credit: 0,
          suppliers: new Map(),
        };
      }
      target.invoice += toNumber(addition.invoice);
      target.credit += toNumber(addition.credit);
      if (addition.suppliers instanceof Map) {
        addition.suppliers.forEach((supplierEntry, supplierId) => {
          const existing = target.suppliers.get(supplierId) || {
            supplierId,
            invoice: 0,
            credit: 0,
            discountAgreed: null,
            discountReceived: null,
            actualTotal: 0,
            retailTotal: 0,
          };
          existing.invoice += toNumber(supplierEntry.invoice);
          existing.credit += toNumber(supplierEntry.credit);
          existing.actualTotal += toNumber(supplierEntry.actualTotal);
          existing.retailTotal += toNumber(supplierEntry.retailTotal);
          if (existing.discountAgreed === null || existing.discountAgreed === undefined) {
            if (supplierEntry.discountAgreed !== null && supplierEntry.discountAgreed !== undefined) {
              existing.discountAgreed = supplierEntry.discountAgreed;
            }
          }
          target.suppliers.set(supplierId, existing);
        });
      }
      return target;
    };

    let otherNominalEntry = null;
    if (panelNominalData instanceof Map) {
      otherNominalEntry = mergeNominalEntries(otherNominalEntry, panelNominalData.get(UNKNOWN_NOMINAL_CODE));
      panelNominalData.forEach((entry, codeId) => {
        if (!entry) return;
        if (codeId === UNKNOWN_NOMINAL_CODE) {
          return;
        }
        if (!knownCategoryCodes.has(codeId)) {
          otherNominalEntry = mergeNominalEntries(otherNominalEntry, entry);
        }
      });
    }

    const categoryRows = breakdownFieldConfig.map((config) => {
      const invoiceValue = toNumber(breakdownRecord[config.invoiceKey]);
      const creditValue = toNumber(breakdownRecord[config.creditKey]);
      const overallValue = invoiceValue + creditValue;
      const { approvedValue, notApprovedValue, blockedValue } = normaliseStatusValues(
        overallValue,
        config.approvedKey ? breakdownRecord[config.approvedKey] : 0,
        config.notApprovedKey ? breakdownRecord[config.notApprovedKey] : 0,
        config.blockedKey ? breakdownRecord[config.blockedKey] : 0
      );
      return {
        key: config.key,
        title: config.title,
        invoiceValue,
        creditValue,
        approvedValue,
        notApprovedValue,
        blockedValue,
        parentKey: config.parentKey || null,
      };
    });

    if (
      otherNominalEntry &&
      (toNumber(otherNominalEntry.invoice) !== 0 ||
        toNumber(otherNominalEntry.credit) !== 0 ||
        (otherNominalEntry.suppliers && otherNominalEntry.suppliers.size > 0))
    ) {
      const overallValue = toNumber(otherNominalEntry.invoice) + toNumber(otherNominalEntry.credit);
      const statusTotals = buildStatusTotalsFromSuppliers(otherNominalEntry.suppliers);
      const { approvedValue, notApprovedValue, blockedValue } = normaliseStatusValues(
        overallValue,
        statusTotals.approvedValue,
        statusTotals.notApprovedValue,
        statusTotals.blockedValue
      );
      categoryRows.push({
        key: 'unknown_nominal_codes',
        title: 'Other',
        invoiceValue: toNumber(otherNominalEntry.invoice),
        creditValue: toNumber(otherNominalEntry.credit),
        approvedValue,
        notApprovedValue,
        blockedValue,
        parentKey: null,
      });
    }

    let displayedCategoryRows = categoryRows;
    if (appliedNominalCodeFilter) {
      const normalizedFilter = normalizeNominalCodeId(appliedNominalCodeFilter);
      const isCarPartsFilter = (categoryCodeSets.car_parts || []).some(
        (code) => normalizeNominalCodeId(code) === normalizedFilter
      );
      const matchesFilter = breakdownFieldConfig.some((config) =>
        (config.codes || []).some((code) => normalizeNominalCodeId(code) === normalizedFilter)
      );
      if (matchesFilter && !isCarPartsFilter) {
        const filteredNominalEntry = panelNominalData?.get(normalizedFilter);
        const matchingConfigs = breakdownFieldConfig.filter((config) =>
          (config.codes || []).some((code) => normalizeNominalCodeId(code) === normalizedFilter)
        );
        const matchedConfig =
          matchingConfigs.find((config) => config.key !== 'sub_contracted') || matchingConfigs[0] || null;
        const matchedCategoryRow = matchedConfig
          ? categoryRows.find((row) => row.key === matchedConfig.key)
          : null;
        const resolvedCode = normalizedFilter === UNKNOWN_NOMINAL_CODE ? '' : normalizedFilter;
        const nominalMeta = nominalCodes.find((nominal) => nominal.nominal_code_id === resolvedCode);
        const filteredRowTitle = nominalMeta?.name
          ? nominalMeta.name
          : formatNominalCodeLabel(normalizedFilter);

        if (matchedCategoryRow) {
          displayedCategoryRows = [
            {
              ...matchedCategoryRow,
              title: filteredRowTitle,
              parentKey: null,
            },
          ];
        } else {
          const filteredInvoice = toNumber(filteredNominalEntry?.invoice);
          const filteredCredit = toNumber(filteredNominalEntry?.credit);
          const overallValue = filteredInvoice + filteredCredit;
          const statusTotals = buildStatusTotalsFromSuppliers(filteredNominalEntry?.suppliers);
          const { approvedValue, notApprovedValue, blockedValue } = normaliseStatusValues(
            overallValue,
            statusTotals.approvedValue,
            statusTotals.notApprovedValue,
            statusTotals.blockedValue
          );
          displayedCategoryRows = [
            {
              key: `filtered_nominal_${normalizedFilter}`,
              title: filteredRowTitle,
              invoiceValue: filteredInvoice,
              creditValue: filteredCredit,
              approvedValue,
              notApprovedValue,
              blockedValue,
              parentKey: null,
            },
          ];
        }
      }
      if (matchesFilter && isCarPartsFilter) {
        displayedCategoryRows = categoryRows.filter(
          (row) => row.key === 'car_parts' || row.parentKey === 'car_parts'
        );
      }
    }

    const displayedCategoryPercentBase = displayedCategoryRows.reduce(
      (sum, row) => (row.parentKey ? sum : sum + row.invoiceValue + row.creditValue),
      0
    );
    const resolveApprovalPercentBase = (row) => {
      if (approvalPercentMode !== 'split') return displayedCategoryPercentBase;
      if (!row.parentKey) return displayedCategoryPercentBase;
      const rowTotal = row.approvedValue + row.notApprovedValue + row.blockedValue;
      return rowTotal || displayedCategoryPercentBase;
    };

    const showCategories = true;
    const categoriesShowingSuppliersDirectly = new Set([
      'car_parts',
      'car_parts_genuine',
      'car_parts_non_genuine',
      'car_parts_green',
      'sub_contracted',
      'sub_contracted_adas_calibration_diagnostics',
      'sub_contracted_general',
      'sub_contracted_lock_set',
      'sub_contracted_signwriting_graphics',
      'sub_contracted_tyres_alloy_wheels',
      'sub_contracted_windscreen_repairs',
      'storage_and_recovery',
      'unknown_nominal_codes',
    ]);

    const selectedCategoryConfig = panelSelectedCategoryKey
      ? breakdownFieldConfig.find((config) => config.key === panelSelectedCategoryKey) || null
      : null;

    const showNominalCodes =
      panelSelectedCategoryKey &&
      panelSelectedCategoryKey !== 'unknown_nominal_codes' &&
      !categoriesShowingSuppliersDirectly.has(panelSelectedCategoryKey) &&
      !panelSelectedNominalCodeId;

    const buildCategoryNominalEntries = () => {
      if (!selectedCategoryConfig || !panelNominalData) return [];
      const codes = selectedCategoryConfig.codes || [];
      if (!codes.length) return [];
      const normalizedCodes = new Set(codes.map((code) => normalizeNominalCodeId(code)));
      const entries = [];
      panelNominalData.forEach((value, codeId) => {
        if (normalizedCodes.has(codeId)) {
          const resolvedCode = codeId === UNKNOWN_NOMINAL_CODE ? '' : codeId;
          const nominalMeta = nominalCodes.find((nominal) => nominal.nominal_code_id === resolvedCode);
          const displayName = nominalMeta?.name || 'Unknown';
          entries.push({
            nominalCodeId: codeId,
            displayId: formatNominalCodeLabel(codeId),
            displayName,
            invoice: value.invoice,
            credit: value.credit,
            suppliers: Array.from(value.suppliers.values()).map((supplier) => ({
              supplierId: supplier.supplierId,
              invoice: supplier.invoice,
              credit: supplier.credit,
              discountAgreed: supplier.discountAgreed,
              discountReceived: supplier.discountReceived,
              actualTotal: supplier.actualTotal,
              retailTotal: supplier.retailTotal,
            })),
          });
        }
      });
      return entries.sort((a, b) => a.displayId.localeCompare(b.displayId));
    };

    const selectedNominalEntry = (() => {
      if (!panelSelectedNominalCodeId || !panelNominalData) return null;
      const entry = panelNominalData.get(panelSelectedNominalCodeId);
      if (!entry) return null;
      const supplierRows = Array.from(entry.suppliers.values()).map((supplier) => {
        const resolvedDiscountAgreed =
          supplier.discountAgreed ?? supplierDiscountMap.get(supplier.supplierId) ?? null;
        const discountReceived = computeDiscountReceived(supplier.actualTotal, supplier.retailTotal);
        return {
          supplierId: supplier.supplierId,
          displayName:
            suppliers?.find((s) => s.supplier_id === supplier.supplierId)?.name || 'Unnamed Supplier',
          invoice: supplier.invoice,
          credit: supplier.credit,
          discountAgreed: resolvedDiscountAgreed,
          discountReceived,
          actualTotal: supplier.actualTotal,
          retailTotal: supplier.retailTotal,
        };
      });
      const resolvedCode = panelSelectedNominalCodeId === UNKNOWN_NOMINAL_CODE ? '' : panelSelectedNominalCodeId;
      const nominalMeta = nominalCodes.find((nominal) => nominal.nominal_code_id === resolvedCode);
      return {
        nominalCodeId: panelSelectedNominalCodeId,
        invoice: entry.invoice,
        credit: entry.credit,
        suppliers: supplierRows,
        displayName: nominalMeta?.name || 'Unknown',
      };
    })();

    const categoryNominalEntries = buildCategoryNominalEntries();
    const categoryNominalPercentBase = categoryNominalEntries.reduce(
      (sum, entry) => sum + entry.invoice + entry.credit,
      0
    );
    const showSuppliers = Boolean(panelSelectedNominalCodeId && selectedNominalEntry);
    const showDirectSuppliers = Boolean(
      panelSelectedCategoryKey &&
      categoriesShowingSuppliersDirectly.has(panelSelectedCategoryKey) &&
      !panelSelectedNominalCodeId
    );
    const nominalSupplierPercentBase = showSuppliers
      ? toNumber(selectedNominalEntry.invoice) + toNumber(selectedNominalEntry.credit)
      : 0;
    const sortedNominalSuppliers = showSuppliers
      ? sortSupplierRows(selectedNominalEntry.suppliers, nominalSupplierPercentBase)
      : [];

    const scrollToRef = (ref) => {
      if (!ref?.current) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    };

    const handleCategorySelect = (categoryKey) => {
      if (!updateCategoryKey) return;
      updateCategoryKey(categoryKey);
      updateNominalCodeId?.(null);
      scrollToRef(panelRef);
    };

    const handleNominalCodeSelect = (nominalCodeId) => {
      updateNominalCodeId?.(nominalCodeId);
      scrollToRef(supplierTableRef);
    };

    const resetToCategories = () => {
      updateCategoryKey?.(null);
      updateNominalCodeId?.(null);
    };

    const resetNominalSelection = () => {
      updateNominalCodeId?.(null);
    };

    const costBreakdownHeaderCellSx = { fontWeight: 600, whiteSpace: 'nowrap' };
    const costBreakdownTableSx = { width: '100%', minWidth: 1100 };
    const costBreakdownTableContainerSx = {
      maxHeight: '65vh',
      overflowX: 'auto',
      overflowY: 'auto',
      width: '100%',
    };
    const panelPaperSx = panelWidth
      ? { width: panelWidth, maxWidth: panelWidth, ml: 'auto', mr: 0, boxSizing: 'border-box' }
      : { width: '100%' };

    return (
      <Paper
        ref={panelRef}
        sx={{
          ...chartPaperSx,
          p: { xs: 2, md: 3 },
          ...panelPaperSx,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              position: 'relative',
              mb: 2,
              minHeight: 56,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                width: '100%',
              }}
            >
              {`Cost Breakdown — ${locationName}`}
            </Typography>
            {(onApprovalPercentModeChange || extraContent) ? (
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                }}
              >
                {onApprovalPercentModeChange ? (
                  <FormControl component="fieldset" sx={{ minWidth: 220 }}>
                    <FormLabel sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                      Supplier Status Split (%)
                    </FormLabel>
                    <RadioGroup
                      row
                      value={approvalPercentMode}
                      onChange={(event) => onApprovalPercentModeChange(event.target.value)}
                    >
                      <FormControlLabel value="weighted" control={<Radio size="small" />} label="Weighted" />
                      <FormControlLabel value="split" control={<Radio size="small" />} label="Split 100%" />
                    </RadioGroup>
                  </FormControl>
                ) : null}
                {extraContent ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>{extraContent}</Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            {panelSelectedNominalCodeId ? (
              <Button size="small" variant="outlined" onClick={resetNominalSelection}>
                Back to Nominal Codes
              </Button>
            ) : null}
            {panelSelectedCategoryKey ? (
              <Button size="small" variant="outlined" onClick={resetToCategories}>
                Back to Categories
              </Button>
            ) : null}
            {showCloseButton && onClose ? (
              <Button size="small" variant="text" onClick={onClose}>
                Close
              </Button>
            ) : null}
          </Box>
        </Box>

        {showCategories && (
          <TableContainer sx={costBreakdownTableContainerSx}>
            <Table size="small" stickyHeader sx={costBreakdownTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={costBreakdownHeaderCellSx}>Category</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Invoice Amount (Net)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Credit Amount (Net)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Overall Amount (Net)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Approved (Net)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Not Approved (Net)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Not Allowed (Net)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Approved Total Spend (%)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Not Approved Total Spend (%)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Not Allowed Total Spend (%)</TableCell>
                  <TableCell sx={costBreakdownHeaderCellSx} align="right">Share of Total Spend (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedCategoryRows.map((row, index) => {
                  const isFilteredNominalRow = row.key.startsWith('filtered_nominal_');
                  const isClickable = isFilteredNominalRow || row.key !== 'sub_contracted';
                  const hasVisibleParent =
                    row.parentKey && displayedCategoryRows.some((candidate) => candidate.key === row.parentKey);
                  const baseRowColor = hasVisibleParent ? '#f8fafc' : '#eef2f7';
                  return (
                  <TableRow
                    key={row.key}
                    hover={isClickable}
                    onClick={() => {
                      if (isFilteredNominalRow) {
                        const targetNominalCodeId = row.key.replace('filtered_nominal_', '');
                        handleNominalCodeSelect(targetNominalCodeId);
                        updateCategoryKey?.(null);
                        return;
                      }
                      if (!isClickable) return;
                      handleCategorySelect(row.key);
                    }}
                    sx={{
                      bgcolor: baseRowColor,
                      cursor: isClickable ? 'pointer' : 'default',
                      ...(isClickable ? { '&:hover': { bgcolor: '#e1e8f0' } } : {}),
                    }}
                  >
                    <TableCell>
                      <Box sx={{ pl: hasVisibleParent ? 3 : 0, fontStyle: hasVisibleParent ? 'italic' : 'normal' }}>
                        {hasVisibleParent ? '↳ ' : ''}
                        {row.title}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.invoiceValue)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.creditValue)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.invoiceValue + row.creditValue)}</TableCell>
                    <>
                      <TableCell align="right">{formatCurrency(row.approvedValue)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.notApprovedValue)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.blockedValue)}</TableCell>
                      {(() => {
                        const approvalPercentBase = resolveApprovalPercentBase(row);
                        const approvedPercent = formatPercentValue(row.approvedValue, approvalPercentBase);
                        const notApprovedPercent = formatPercentValue(row.notApprovedValue, approvalPercentBase);
                        const blockedPercent = formatPercentValue(row.blockedValue, approvalPercentBase);
                        const approvedRounded = Number(approvedPercent.toFixed(2));
                        const notApprovedRounded = Number(notApprovedPercent.toFixed(2));
                        const blockedRounded = Number(blockedPercent.toFixed(2));
                        const shareRounded = Number(
                          formatPercentValue(row.invoiceValue + row.creditValue, displayedCategoryPercentBase).toFixed(2)
                        );
                        return (
                          <>
                            <TableCell align="right">{`${approvedRounded.toFixed(2)}%`}</TableCell>
                            <TableCell align="right">{`${notApprovedRounded.toFixed(2)}%`}</TableCell>
                            <TableCell align="right">{`${blockedRounded.toFixed(2)}%`}</TableCell>
                            <TableCell align="right">{`${shareRounded.toFixed(2)}%`}</TableCell>
                          </>
                        );
                      })()}
                    </>
                  </TableRow>
                );
                })}
                <TableRow sx={stickyTotalRowSx}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      displayedCategoryRows.reduce((sum, row) => (
                        row.parentKey ? sum : sum + row.invoiceValue
                      ), 0)
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      displayedCategoryRows.reduce((sum, row) => (
                        row.parentKey ? sum : sum + row.creditValue
                      ), 0)
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      displayedCategoryRows.reduce((sum, row) => (
                        row.parentKey ? sum : sum + row.invoiceValue + row.creditValue
                      ), 0)
                    )}
                  </TableCell>
                  <>
                    <TableCell align="right">
                      {formatCurrency(
                        displayedCategoryRows.reduce((sum, row) => (
                          row.parentKey ? sum : sum + row.approvedValue
                        ), 0)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(
                        displayedCategoryRows.reduce((sum, row) => (
                          row.parentKey ? sum : sum + row.notApprovedValue
                        ), 0)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(
                        displayedCategoryRows.reduce((sum, row) => (
                          row.parentKey ? sum : sum + row.blockedValue
                        ), 0)
                      )}
                    </TableCell>
                    {(() => {
                      const invoiceTotal = displayedCategoryRows.reduce(
                        (sum, row) => (row.parentKey ? sum : sum + row.invoiceValue),
                        0
                      );
                      const creditTotal = displayedCategoryRows.reduce(
                        (sum, row) => (row.parentKey ? sum : sum + row.creditValue),
                        0
                      );
                      const approvedTotal = displayedCategoryRows.reduce(
                        (sum, row) => (row.parentKey ? sum : sum + row.approvedValue),
                        0
                      );
                      const notApprovedTotal = displayedCategoryRows.reduce(
                        (sum, row) => (row.parentKey ? sum : sum + row.notApprovedValue),
                        0
                      );
                      const blockedTotal = displayedCategoryRows.reduce(
                        (sum, row) => (row.parentKey ? sum : sum + row.blockedValue),
                        0
                      );
                      const approvedPercent = formatPercentValue(approvedTotal, displayedCategoryPercentBase);
                      const notApprovedPercent = formatPercentValue(notApprovedTotal, displayedCategoryPercentBase);
                      const blockedPercent = formatPercentValue(blockedTotal, displayedCategoryPercentBase);
                      const approvedRounded = Number(approvedPercent.toFixed(2));
                      const notApprovedRounded = Number(notApprovedPercent.toFixed(2));
                      const blockedRounded = Number(blockedPercent.toFixed(2));
                      const shareRounded = Number(
                        formatPercentValue(invoiceTotal + creditTotal, displayedCategoryPercentBase).toFixed(2)
                      );
                        return (
                          <>
                            <TableCell align="right">{`${approvedRounded.toFixed(2)}%`}</TableCell>
                            <TableCell align="right">{`${notApprovedRounded.toFixed(2)}%`}</TableCell>
                            <TableCell align="right">{`${blockedRounded.toFixed(2)}%`}</TableCell>
                            <TableCell align="right">{`${shareRounded.toFixed(2)}%`}</TableCell>
                          </>
                        );
                      })()}
                  </>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {showNominalCodes && (
          <Box sx={{ mt: 3 }} ref={nominalTableRef}>
            <Box sx={{ px: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {selectedCategoryConfig?.title || 'Nominal Codes'}
              </Typography>
            </Box>
            {categoryNominalEntries.length ? (
              <TableContainer sx={costBreakdownTableContainerSx}>
                <Table size="small" stickyHeader sx={costBreakdownTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={costBreakdownHeaderCellSx}>Nominal Name / Category</TableCell>
                      <TableCell sx={costBreakdownHeaderCellSx}>Nominal Code</TableCell>
                      <TableCell sx={costBreakdownHeaderCellSx} align="right">Invoice Amount (Net)</TableCell>
                      <TableCell sx={costBreakdownHeaderCellSx} align="right">Credit Amount (Net)</TableCell>
                      <TableCell sx={costBreakdownHeaderCellSx} align="right">Overall Amount (Net)</TableCell>
                      <TableCell sx={costBreakdownHeaderCellSx} align="right">Share of Total Spend (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryNominalEntries.map((entry) => (
                      <TableRow
                        key={entry.nominalCodeId}
                        hover
                        onClick={() => handleNominalCodeSelect(entry.nominalCodeId)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#eef2f7' },
                        }}
                      >
                        <TableCell>{entry.displayName}</TableCell>
                        <TableCell>{entry.displayId}</TableCell>
                        <TableCell align="right">{formatCurrency(entry.invoice)}</TableCell>
                        <TableCell align="right">{formatCurrency(entry.credit)}</TableCell>
                        <TableCell align="right">{formatCurrency(entry.invoice + entry.credit)}</TableCell>
                        <TableCell align="right">
                          {formatPercent(
                            entry.invoice + entry.credit,
                            categoryNominalPercentBase
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={stickyTotalRowSx}>
                      <TableCell>Total</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        {formatCurrency(
                          categoryNominalEntries.reduce((sum, entry) => sum + entry.invoice, 0)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          categoryNominalEntries.reduce((sum, entry) => sum + entry.credit, 0)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          categoryNominalEntries.reduce(
                            (sum, entry) => sum + entry.invoice + entry.credit,
                            0
                          )
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatPercent(
                          categoryNominalPercentBase,
                          categoryNominalPercentBase
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">No nominal codes found for this category.</Typography>
            )}
          </Box>
        )}

        {showDirectSuppliers && (
          <Box sx={{ mt: 3 }} ref={supplierTableRef}>
            <Box sx={{ px: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {selectedCategoryConfig?.title || 'Suppliers'}
              </Typography>
            </Box>
            {panelNominalData ? (() => {
              const filteredEntries = Array.from(panelNominalData.entries()).filter(([codeId]) => {
                if (panelSelectedCategoryKey === 'unknown_nominal_codes') {
                  return codeId === UNKNOWN_NOMINAL_CODE || !knownCategoryCodes.has(codeId);
                }
                const normalized = normalizeNominalCodeId(codeId);
                const config = selectedCategoryConfig;
                if (!config) return false;
                if (config.key === 'sub_contracted') {
                  return (categoryCodeSets.sub_contracted || []).includes(normalized);
                }
                return config.codes.includes(normalized);
              });

              let categoryNetTotal = filteredEntries.reduce(
                (sum, [, entry]) => sum + toNumber(entry.invoice) + toNumber(entry.credit),
                0
              );

              const supplierMap = new Map();
              const unassignedKey = 'unassigned_supplier';

              filteredEntries.forEach(([codeId, entry]) => {
                const suppliersList = Array.from(entry.suppliers.values());
                let suppliersInvoice = 0;
                let suppliersCredit = 0;

                suppliersList.forEach((supplier) => {
                  const supplierId = supplier.supplierId;
                  const supplierName =
                    suppliers?.find((s) => s.supplier_id === supplierId)?.name || 'Unnamed Supplier';
                  const existing = supplierMap.get(supplierId) || {
                    key: `${codeId}-${supplierId}`,
                    supplierId,
                    displayName: supplierName,
                    invoice: 0,
                    credit: 0,
                    discountAgreed: null,
                    discountReceived: null,
                    actualTotal: 0,
                    retailTotal: 0,
                  };
                  existing.invoice += toNumber(supplier.invoice);
                  existing.credit += toNumber(supplier.credit);
                  existing.actualTotal += toNumber(supplier.actualTotal);
                  existing.retailTotal += toNumber(supplier.retailTotal);
                  if (existing.discountAgreed === null || existing.discountAgreed === undefined) {
                    if (supplier.discountAgreed !== null && supplier.discountAgreed !== undefined) {
                      existing.discountAgreed = supplier.discountAgreed;
                    }
                  }
                  existing.discountReceived = computeDiscountReceived(existing.actualTotal, existing.retailTotal);
                  suppliersInvoice += toNumber(supplier.invoice);
                  suppliersCredit += toNumber(supplier.credit);
                  supplierMap.set(supplierId, existing);
                });

                if (panelSelectedCategoryKey === 'unknown_nominal_codes') {
                  const entryInvoice = toNumber(entry.invoice);
                  const entryCredit = toNumber(entry.credit);
                  const deltaInvoice = entryInvoice - suppliersInvoice;
                  const deltaCredit = entryCredit - suppliersCredit;
                  if (Math.abs(deltaInvoice) > 0.000001 || Math.abs(deltaCredit) > 0.000001) {
                    const existing = supplierMap.get(unassignedKey) || {
                      key: `${codeId}-${unassignedKey}`,
                      supplierId: '',
                      displayName: 'Unassigned Supplier',
                      invoice: 0,
                      credit: 0,
                      discountAgreed: null,
                      discountReceived: null,
                      actualTotal: 0,
                      retailTotal: 0,
                    };
                    existing.invoice += deltaInvoice;
                    existing.credit += deltaCredit;
                    supplierMap.set(unassignedKey, existing);
                  }
                }
              });

              let supplierRows = Array.from(supplierMap.values()).map((supplier) => {
                const resolvedDiscountAgreed =
                  supplier.discountAgreed ?? supplierDiscountMap.get(supplier.supplierId) ?? null;
                return {
                  ...supplier,
                  discountAgreed: resolvedDiscountAgreed,
                  discountReceived: computeDiscountReceived(supplier.actualTotal, supplier.retailTotal),
                };
              });

              if (
                panelSelectedCategoryKey === 'car_parts_genuine' ||
                panelSelectedCategoryKey === 'car_parts_non_genuine' ||
                panelSelectedCategoryKey === 'car_parts_green'
              ) {
                const targetCategory = panelSelectedCategoryKey.replace('car_parts_', '');
                supplierRows = supplierRows.filter(
                  (supplier) => supplierCategoryMap.get(supplier.supplierId) === targetCategory
                );
                categoryNetTotal = supplierRows.reduce(
                  (sum, supplier) => sum + toNumber(supplier.invoice) + toNumber(supplier.credit),
                  0
                );
              }

              if (panelSelectedCategoryKey === 'unknown_nominal_codes') {
                categoryNetTotal = supplierRows.reduce(
                  (sum, supplier) => sum + toNumber(supplier.invoice) + toNumber(supplier.credit),
                  0
                );
              }

              const approvedSupplierRows = supplierRows.filter(
                (supplier) => supplierStatusMap.get(supplier.supplierId) === 'approved'
              );
              const notApprovedSupplierRows = supplierRows.filter(
                (supplier) => supplierStatusMap.get(supplier.supplierId) === 'not_approved'
              );
              const blockedSupplierRows = supplierRows.filter(
                (supplier) => NOT_ALLOWED_SUPPLIER_STATUSES.has(supplierStatusMap.get(supplier.supplierId))
              );
              const unclassifiedSupplierRows = supplierRows.filter((supplier) => {
                const supplierStatus = supplierStatusMap.get(supplier.supplierId);
                return (
                  supplierStatus !== 'approved' &&
                  supplierStatus !== 'not_approved' &&
                  !NOT_ALLOWED_SUPPLIER_STATUSES.has(supplierStatus)
                );
              });

              const sortedSupplierRows = sortSupplierRows(supplierRows, categoryNetTotal);

              const invoiceTotal = supplierRows.reduce((sum, supplier) => sum + toNumber(supplier.invoice), 0);
              const creditTotal = supplierRows.reduce((sum, supplier) => sum + toNumber(supplier.credit), 0);
              const approvedTotal = approvedSupplierRows.reduce(
                (sum, supplier) => sum + toNumber(supplier.invoice) + toNumber(supplier.credit),
                0
              );
              const notApprovedTotal = notApprovedSupplierRows.reduce(
                (sum, supplier) => sum + toNumber(supplier.invoice) + toNumber(supplier.credit),
                0
              );
              const blockedTotal = blockedSupplierRows.reduce(
                (sum, supplier) => sum + toNumber(supplier.invoice) + toNumber(supplier.credit),
                0
              );
              const unclassifiedTotal = unclassifiedSupplierRows.reduce(
                (sum, supplier) => sum + toNumber(supplier.invoice) + toNumber(supplier.credit),
                0
              );

              return (
                <TableContainer
                  sx={{
                    ...costBreakdownTableContainerSx,
                    maxHeight: supplierRows.length > 10 ? 360 : 'auto',
                    overflowY: supplierRows.length > 10 ? 'auto' : 'visible',
                  }}
                >
                  <Table size="small" stickyHeader sx={costBreakdownTableSx}>
                    <TableHead>
                      <TableRow>
                        {renderSupplierHeaderCell('Supplier', 'supplier')}
                        {renderSupplierHeaderCell('Supplier Code', 'code')}
                        {renderSupplierHeaderCell('Invoice Amount (Net)', 'invoice')}
                        {renderSupplierHeaderCell('Credit Amount (Net)', 'credit')}
                        {renderSupplierHeaderCell('Overall Amount (Net)', 'total')}
                        {renderSupplierHeaderCell('Discount Agreed (%)', 'discountAgreed')}
                        {renderSupplierHeaderCell('Discount Received (%)', 'discountReceived')}
                        {renderSupplierHeaderCell('Share of Total Spend (%)', 'percent')}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Approved</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>{formatCurrency(approvedTotal)}</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell>{formatPercent(approvedTotal, categoryNetTotal)}</TableCell>
                      </TableRow>
                      {sortSupplierRows(approvedSupplierRows, categoryNetTotal).map((row) => (
                        <TableRow key={`approved-${row.key}`}>
                          <TableCell>{row.displayName}</TableCell>
                          <TableCell>{row.supplierId}</TableCell>
                          <TableCell>{formatCurrency(row.invoice)}</TableCell>
                          <TableCell>{formatCurrency(row.credit)}</TableCell>
                          <TableCell>{formatCurrency(row.invoice + row.credit)}</TableCell>
                          <TableCell>{formatDiscountPercent(row.discountAgreed)}</TableCell>
                          <TableCell>{formatDiscountPercent(row.discountReceived)}</TableCell>
                          <TableCell>{formatPercent(row.invoice + row.credit, categoryNetTotal)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Not Approved</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>{formatCurrency(notApprovedTotal)}</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell>{formatPercent(notApprovedTotal, categoryNetTotal)}</TableCell>
                      </TableRow>
                      {sortSupplierRows(notApprovedSupplierRows, categoryNetTotal).map((row) => (
                        <TableRow key={`not-approved-${row.key}`}>
                          <TableCell>{row.displayName}</TableCell>
                          <TableCell>{row.supplierId}</TableCell>
                          <TableCell>{formatCurrency(row.invoice)}</TableCell>
                          <TableCell>{formatCurrency(row.credit)}</TableCell>
                          <TableCell>{formatCurrency(row.invoice + row.credit)}</TableCell>
                          <TableCell>{formatDiscountPercent(row.discountAgreed)}</TableCell>
                          <TableCell>{formatDiscountPercent(row.discountReceived)}</TableCell>
                          <TableCell>{formatPercent(row.invoice + row.credit, categoryNetTotal)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Not Allowed</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>{formatCurrency(blockedTotal)}</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell>{formatPercent(blockedTotal, categoryNetTotal)}</TableCell>
                      </TableRow>
                      {sortSupplierRows(blockedSupplierRows, categoryNetTotal).map((row) => (
                        <TableRow key={`blocked-${row.key}`}>
                          <TableCell>{row.displayName}</TableCell>
                          <TableCell>{row.supplierId}</TableCell>
                          <TableCell>{formatCurrency(row.invoice)}</TableCell>
                          <TableCell>{formatCurrency(row.credit)}</TableCell>
                          <TableCell>{formatCurrency(row.invoice + row.credit)}</TableCell>
                          <TableCell>{formatDiscountPercent(row.discountAgreed)}</TableCell>
                          <TableCell>{formatDiscountPercent(row.discountReceived)}</TableCell>
                          <TableCell>{formatPercent(row.invoice + row.credit, categoryNetTotal)}</TableCell>
                        </TableRow>
                      ))}
                      {unclassifiedSupplierRows.length > 0 && (
                        <>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Unclassified</TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell>{formatCurrency(unclassifiedTotal)}</TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell>{formatPercent(unclassifiedTotal, categoryNetTotal)}</TableCell>
                          </TableRow>
                          {sortSupplierRows(unclassifiedSupplierRows, categoryNetTotal).map((row) => (
                            <TableRow key={`unclassified-${row.key}`}>
                              <TableCell>{row.displayName}</TableCell>
                              <TableCell>{row.supplierId}</TableCell>
                              <TableCell>{formatCurrency(row.invoice)}</TableCell>
                              <TableCell>{formatCurrency(row.credit)}</TableCell>
                              <TableCell>{formatCurrency(row.invoice + row.credit)}</TableCell>
                              <TableCell>{formatDiscountPercent(row.discountAgreed)}</TableCell>
                              <TableCell>{formatDiscountPercent(row.discountReceived)}</TableCell>
                              <TableCell>{formatPercent(row.invoice + row.credit, categoryNetTotal)}</TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}
                      <TableRow sx={stickyTotalRowSx}>
                        <TableCell>Total</TableCell>
                        <TableCell />
                        <TableCell>{formatCurrency(invoiceTotal)}</TableCell>
                        <TableCell>{formatCurrency(creditTotal)}</TableCell>
                        <TableCell>{formatCurrency(invoiceTotal + creditTotal)}</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell>{formatPercent(categoryNetTotal, categoryNetTotal)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })() : (
              <Typography color="text.secondary">No supplier data available.</Typography>
            )}
          </Box>
        )}
        {showSuppliers && selectedNominalEntry && (
          <Box sx={{ mt: 3 }} ref={supplierTableRef}>
            <Box sx={{ px: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {`Suppliers — ${selectedNominalEntry.displayName || selectedNominalDisplay}`}
              </Typography>
            </Box>
            {selectedNominalEntry.suppliers.length ? (
              <TableContainer
                sx={{
                  ...costBreakdownTableContainerSx,
                  maxHeight: selectedNominalEntry.suppliers.length > 10 ? 360 : 'auto',
                  overflowY: selectedNominalEntry.suppliers.length > 10 ? 'auto' : 'visible',
                }}
              >
                <Table size="small" stickyHeader sx={costBreakdownTableSx}>
                  <TableHead>
                      <TableRow>
                        {renderSupplierHeaderCell('Supplier', 'supplier')}
                        {renderSupplierHeaderCell('Supplier Code', 'code')}
                        {renderSupplierHeaderCell('Invoice Amount (Net)', 'invoice')}
                        {renderSupplierHeaderCell('Credit Amount (Net)', 'credit')}
                        {renderSupplierHeaderCell('Overall Amount (Net)', 'total')}
                        {renderSupplierHeaderCell('Discount Agreed (%)', 'discountAgreed')}
                        {renderSupplierHeaderCell('Discount Received (%)', 'discountReceived')}
                        {renderSupplierHeaderCell('Share of Total Spend (%)', 'percent')}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {sortedNominalSuppliers.map((supplier) => (
                      <TableRow key={supplier.supplierId}>
                        <TableCell>{supplier.displayName}</TableCell>
                        <TableCell>{supplier.supplierId}</TableCell>
                        <TableCell>{formatCurrency(supplier.invoice)}</TableCell>
                        <TableCell>{formatCurrency(supplier.credit)}</TableCell>
                        <TableCell>{formatCurrency(supplier.invoice + supplier.credit)}</TableCell>
                        <TableCell>{formatDiscountPercent(supplier.discountAgreed)}</TableCell>
                        <TableCell>{formatDiscountPercent(supplier.discountReceived)}</TableCell>
                        <TableCell>
                          {formatPercent(
                            supplier.invoice + supplier.credit,
                            selectedNominalEntry.invoice + selectedNominalEntry.credit
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={stickyTotalRowSx}>
                      <TableCell>Total</TableCell>
                      <TableCell />
                      <TableCell>
                        {formatCurrency(
                          selectedNominalEntry.suppliers.reduce((sum, supplier) => sum + supplier.invoice, 0)
                        )}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(
                          selectedNominalEntry.suppliers.reduce((sum, supplier) => sum + supplier.credit, 0)
                        )}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(
                          selectedNominalEntry.suppliers.reduce(
                            (sum, supplier) => sum + supplier.invoice + supplier.credit,
                            0
                          )
                        )}
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{formatPercent(
                        selectedNominalEntry.suppliers.reduce(
                          (sum, supplier) => sum + supplier.invoice + supplier.credit,
                          0
                        ),
                        selectedNominalEntry.invoice + selectedNominalEntry.credit
                      )}</TableCell>
                    </TableRow>
                  </TableBody>
                  </Table>
                </TableContainer>
            ) : (
              <Typography color="text.secondary">No suppliers found for this nominal code.</Typography>
            )}
          </Box>
        )}

        {selectedCategoryKey &&
          !showNominalCodes &&
          !showSuppliers &&
          !showDirectSuppliers &&
          (!panelNominalData || panelNominalData.size === 0) && (
            <Typography color="text.secondary">No data available for this selection.</Typography>
          )}
      </Paper>
    );
  };

  const renderDrilldownPanel = () => {
    if (!selectedLocationId) return null;
    const location = locationDataMap.get(selectedLocationId);
    if (!location) return null;
    const locationName = locationNameMap.get(selectedLocationId) || selectedLocationId;
    const breakdownRecord = selectedLocationBreakdown || {};
    const locationNetTotal =
      toNumber(location.aggregate_invoice_amount) + toNumber(location.aggregate_credit_note_amount);
    return renderCostBreakdownPanel({
      locationName,
      breakdownRecord,
      aggregatedNominalData,
      locationNetTotal,
      selectedCategoryKey,
      setSelectedCategoryKey,
      selectedNominalCodeId,
      setSelectedNominalCodeId,
      nominalTableRef: locationNominalTableRef,
      supplierTableRef: locationSupplierTableRef,
      panelRef: locationBreakdownRef,
      onClose: handleCloseDrilldown,
      appliedNominalCodeFilter: (toFilterArray(appliedFilters.nominalCodeId).length === 1 ? toFilterArray(appliedFilters.nominalCodeId)[0] : ''),
      showCloseButton: true,
      panelWidth: breakdownPanelWidth,
      approvalPercentMode,
      onApprovalPercentModeChange: setApprovalPercentMode,
      extraContent: (
        <Button
          size="small"
          variant="contained"
          onClick={() => handleExportSingleLocationCostBreakdown(selectedLocationId, locationName)}
          disabled={exportingBreakdown}
        >
          {exportingBreakdown ? 'Downloading…' : 'Download CSV'}
        </Button>
      ),
    });
  };

  const renderAggregateCostBreakdown = () => {
    if (
      !isAggregateBreakdownVisible ||
      !aggregatedBreakdownRecord ||
      !aggregatedNominalDataAll ||
      aggregatedNetTotal === 0
    ) {
      return null;
    }
    const exportDisabled =
      exportingBreakdown || budgetsLoading || orderedLocationSummaries.length === 0;
    return (
      <Box sx={{ mt: 3, mb: 3, width: '100%' }}>
        {renderCostBreakdownPanel({
          locationName: 'All Sites',
          breakdownRecord: aggregatedBreakdownRecord,
          aggregatedNominalData: aggregatedNominalDataAll,
          locationNetTotal: aggregatedNetTotal,
          selectedCategoryKey: globalCategoryKey,
          setSelectedCategoryKey: setGlobalCategoryKey,
          selectedNominalCodeId: globalNominalCodeId,
          setSelectedNominalCodeId: setGlobalNominalCodeId,
          nominalTableRef: aggregateNominalTableRef,
          supplierTableRef: aggregateSupplierTableRef,
          panelRef: aggregateBreakdownRef,
          onClose: null,
          appliedNominalCodeFilter: (toFilterArray(appliedFilters.nominalCodeId).length === 1 ? toFilterArray(appliedFilters.nominalCodeId)[0] : ''),
          showCloseButton: false,
          panelWidth: breakdownPanelWidth,
          approvalPercentMode,
          onApprovalPercentModeChange: setApprovalPercentMode,
          extraContent: (
            <Button
              size="small"
              variant="contained"
              onClick={handleExportAllLocationsCostBreakdown}
              disabled={exportDisabled}
            >
              {exportingBreakdown ? 'Downloading…' : 'Download CSV'}
            </Button>
          ),
        })}
      </Box>
    );
  };
  const renderFiltersAndCharts = ({ showCharts = true } = {}) => (
    <>
      <Paper ref={filterCardRef} sx={chartPaperSx}>
        <Grid container spacing={2} columns={{ xs: 12, md: 24 }}>
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" gutterBottom>
              Region
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              value={selectedRegionOptions}
              options={availableRegions}
              getOptionLabel={(option) => {
                if (!option) return '';
                const id = option.region_id || '';
                const name = option.name || '';
                return id ? `${id} - ${name}` : name;
              }}
              isOptionEqualToValue={(option, value) => option.region_id === value?.region_id}
              onChange={(_, newValue) => {
                const nextRegionIds = newValue.map((region) => region.region_id);
                const selectedRegionSet = new Set(nextRegionIds);
                const nextLocationIds = toFilterArray(filtersRef.current.locationId).filter((locationId) => {
                  const selectedLocation = locations.find((location) => location.location_id === locationId);
                  return !selectedLocation || selectedRegionSet.size === 0 || selectedRegionSet.has(selectedLocation.region_id);
                });

                const updatedFilters = {
                  ...filtersRef.current,
                  regionId: nextRegionIds,
                  locationId: nextLocationIds,
                };

                filtersRef.current = updatedFilters;
                setFilters(updatedFilters);
                handleApplyFilters(false);
              }}
              renderInput={(params) => <TextField {...params} placeholder="All Regions" fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" gutterBottom>
              Location
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              value={selectedLocationOptions}
              options={filteredLocations}
              getOptionLabel={(option) => {
                if (!option) return '';
                const id = option.location_id || '';
                const name = option.name || '';
                return id ? `${id} - ${name}` : name;
              }}
              isOptionEqualToValue={(option, value) => option.location_id === value?.location_id}
              onChange={(_, newValue) =>
                handleFilterChange('locationId', newValue.map((location) => location.location_id), {
                  triggerApply: true,
                  usePageSpinner: false,
                })
              }
              renderInput={(params) => <TextField {...params} placeholder="All Locations" fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" gutterBottom>
              Supplier
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              value={selectedSupplierOptions}
              options={supplierFilterOptions}
              getOptionLabel={(option) => {
                if (!option) return '';
                const id = option.supplier_id || '';
                const name = option.name || '';
                if (id === UNNAMED_FILTER_VALUE) return UNNAMED_SUPPLIER_LABEL;
                return id ? `${id} - ${name}` : name;
              }}
              isOptionEqualToValue={(option, value) => option.supplier_id === value?.supplier_id}
              onChange={(_, newValue) =>
                handleFilterChange('supplierId', newValue.map((supplier) => supplier.supplier_id), {
                  triggerApply: true,
                  usePageSpinner: false,
                })
              }
              renderInput={(params) => <TextField {...params} placeholder="All Suppliers" fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" gutterBottom>
              Nominal Code
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              value={selectedNominalCodeOptions}
              options={nominalCodeFilterOptions}
              getOptionLabel={(option) => {
                if (!option) return '';
                const id = option.nominal_code_id || '';
                const name = option.name || '';
                if (id === UNNAMED_FILTER_VALUE) return UNNAMED_NOMINAL_CODE_LABEL;
                return id ? `${id} - ${name}` : name;
              }}
              isOptionEqualToValue={(option, value) => option.nominal_code_id === value?.nominal_code_id}
              onChange={(_, newValue) =>
                handleFilterChange('nominalCodeId', newValue.map((nominalCode) => nominalCode.nominal_code_id), {
                  triggerApply: true,
                  usePageSpinner: false,
                })
              }
              renderInput={(params) => <TextField {...params} placeholder="All Nominal Codes" fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" gutterBottom>
              Granularity
            </Typography>
            <TextField
              select
              fullWidth
              value={filters.granularity}
              onChange={(event) =>
                handleFilterChange('granularity', event.target.value, {
                  triggerApply: true,
                  usePageSpinner: false,
                })
              }
              disabled={granularityOptions.length === 0}
            >
              {granularityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" gutterBottom>
              Date Range
            </Typography>
            <ToggleButtonGroup
              value={quickRange}
              exclusive
              onChange={handleQuickRangeChange}
              size="small"
              sx={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}
            >
              {QUICK_RANGE_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" gutterBottom>
              Start Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={filters.startDate}
              onChange={(event) => handleDateChange('startDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" gutterBottom>
              End Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={filters.endDate}
              onChange={(event) => handleDateChange('endDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" gutterBottom>
              Cost By
            </Typography>
            <ToggleButtonGroup
              value={filters.costBy}
              exclusive
              onChange={handleCostByToggle}
              size="small"
              sx={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}
            >
              {COST_BY_FILTER_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
              </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" gutterBottom>
              Sort Locations
            </Typography>
            <ToggleButtonGroup
              value={locationSortMode}
              exclusive
              onChange={(_event, newMode) => {
                if (!newMode) return;
                setLocationSortMode(newMode);
              }}
              size="small"
              sx={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}
            >
              {LOCATION_SORT_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          {activeTab === 'insurer' && (
            <>
              <Grid item xs={12} md={24}>
                <Typography variant="subtitle1" gutterBottom>
                  Insurer Breakdown Filters
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={insurerFilterOptions.insurers}
                  value={findFilterOption(insurerFilterOptions.insurers, insurerFilters.insurerName)}
                  onChange={(_event, newValue) =>
                    setInsurerFilters((prev) => ({
                      ...prev,
                      insurerName: newValue?.value || '',
                    }))
                  }
                  getOptionLabel={(option) => option.label || option.value || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => <TextField {...params} label="Insurer" placeholder="All Insurers" />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={insurerFilterOptions.manufacturers}
                  value={findFilterOption(
                    insurerFilterOptions.manufacturers,
                    insurerFilters.vehicleManufacturer
                  )}
                  onChange={(_event, newValue) =>
                    setInsurerFilters((prev) => ({
                      ...prev,
                      vehicleManufacturer: newValue?.value || '',
                    }))
                  }
                  getOptionLabel={(option) => option.label || option.value || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField {...params} label="Manufacturer" placeholder="All Manufacturers" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={insurerFilterOptions.driveables}
                  value={findFilterOption(insurerFilterOptions.driveables, insurerFilters.driveable)}
                  onChange={(_event, newValue) =>
                    setInsurerFilters((prev) => ({
                      ...prev,
                      driveable: newValue?.value || '',
                    }))
                  }
                  getOptionLabel={(option) => option.label || option.value || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => <TextField {...params} label="Driveable" placeholder="All" />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={insurerFilterOptions.jobTypes}
                  value={findFilterOption(insurerFilterOptions.jobTypes, insurerFilters.jobType)}
                  onChange={(_event, newValue) =>
                    setInsurerFilters((prev) => ({
                      ...prev,
                      jobType: newValue?.value || '',
                    }))
                  }
                  getOptionLabel={(option) => option.label || option.value || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => <TextField {...params} label="Job Type" placeholder="All" />}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={24}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={handleClear} disabled={optionsLoading}>
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={() => handleApplyFilters(false)}
                disabled={isApplyDisabled || optionsLoading}
              >
                Apply Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {showCharts && (
        <Box
          sx={{
            position: 'relative',
            mt: chartsLoading || loading ? 4 : 0,
            minHeight: displayedLocationCharts.length > 0 ? undefined : (hasAttemptedLoad ? 200 : undefined),
          }}
        >
          {(chartsLoading || loading) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2,
              }}
            >
              <CircularProgress size={32} thickness={4} />
            </Box>
          )}

          <Box sx={{ position: 'relative', zIndex: 1, pointerEvents: chartsLoading || loading ? 'none' : 'auto' }}>
            {displayedLocationCharts.length > 0 ? (
              <>
                {renderAggregateCostBreakdown()}
                {renderLocationSummaryHistogram(orderedLocationSummaries)}
                <Fade
                  in={Boolean(selectedLocationId) && isDrilldownVisible}
                  timeout={{ enter: 300, exit: 400 }}
                  mountOnEnter
                  unmountOnExit
                  onExited={handleDrilldownExited}
                >
                  <Box sx={{ mt: 3, mb: 3, width: '100%', ml: 'auto' }} ref={drilldownRef}>
                    {selectedLocationId ? renderDrilldownPanel() : null}
                  </Box>
                </Fade>
                {displayedLocationCharts.map(renderHistogram)}
              </>
            ) : hasAttemptedLoad && !chartsLoading ? (
              <Paper sx={{ ...chartPaperSx, p: { xs: 2, md: 3 } }}>
                <Typography variant="body1" color="text.secondary">
                  No dashboard data to display. Adjust the filters and click Apply Filters to refresh.
                </Typography>
              </Paper>
            ) : null}
          </Box>
        </Box>
      )}
    </>
  );

  const insurerBaseFilters = useMemo(
    () => ({
      regionId: appliedFilters.regionId,
      locationId: appliedFilters.locationId,
      supplierId: appliedFilters.supplierId,
      startDate: appliedFilters.startDate,
      endDate: appliedFilters.endDate,
      costBy: appliedFilters.costBy,
      nominalCodeId: appliedFilters.nominalCodeId,
    }),
    [appliedFilters]
  );

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(_event, nextTab) => {
            if (nextTab) {
              setActiveTab(nextTab);
              const searchParams = new URLSearchParams(location.search);
              searchParams.set('tab', nextTab);
              navigate(
                {
                  pathname: location.pathname,
                  search: `?${searchParams.toString()}`,
                },
                { replace: true }
              );
            }
          }}
          size="small"
        >
          <ToggleButton value="dashboard">COST BREAKDOWN</ToggleButton>
          {canViewProfitBreakdown && <ToggleButton value="profit">Profit Breakdown</ToggleButton>}
          {canViewBreakdownTabs && <ToggleButton value="insurer">Insurer Breakdown</ToggleButton>}
          {canViewBreakdownTabs && <ToggleButton value="sap">SAP BREAKDOWN</ToggleButton>}
        </ToggleButtonGroup>
      </Box>
      {renderFiltersAndCharts({ showCharts: activeTab === 'dashboard' })}
      {activeTab === 'dashboard' && hoverTooltip.visible && (
        <Box
          sx={{
            position: 'fixed',
            top: hoverTooltip.y,
            left: hoverTooltip.x,
            background: 'rgba(33, 33, 33, 0.95)',
            color: '#fff',
            px: 1.5,
            py: 1,
            borderRadius: 1,
            fontSize: `${CHART_FONT_SIZES.axis}px`,
            pointerEvents: 'none',
            zIndex: 1300,
            boxShadow: 3,
            minWidth: 240,
          }}
        >
          {hoverTooltip.content?.header ? (
            <Typography align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
              {hoverTooltip.content.header}
            </Typography>
          ) : null}
          {hoverTooltip.content?.lines?.map((line, index) => (
            <Typography key={index} align="left" sx={{ display: 'block' }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}
      {canViewProfitBreakdown && activeTab === 'profit' && (
        <Box>
          <ProfitBreakdownTab
            baseFilters={insurerBaseFilters}
            panelWidth={breakdownPanelWidth}
          />
        </Box>
      )}
      {canViewBreakdownTabs && activeTab === 'insurer' && (
        <Box>
          <InsurerBreakdownTab
            baseFilters={insurerBaseFilters}
            nominalCodes={nominalCodes}
            panelWidth={breakdownPanelWidth}
            filters={insurerFilters}
            onFiltersChange={setInsurerFilters}
            onFilterOptionsChange={setInsurerFilterOptions}
          />
        </Box>
      )}
      {canViewBreakdownTabs && activeTab === 'sap' && (
        <Box>
          <SapBreakdownTab baseFilters={insurerBaseFilters} panelWidth={breakdownPanelWidth} />
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
